/**
 * Appels à l'API Stafa.
 *
 * `lireMoi` est la **seconde** des deux étapes de la connexion : un jeton valide
 * ne prouve que l'identité, c'est ici qu'on apprend si la personne a le droit
 * d'entrer. Les autres fonctions servent les écrans métier.
 *
 * Les refus sont reconnus par leur **code**, jamais par leur message : le champ
 * `erreur` est du français destiné aux journaux, il peut être reformulé.
 */

import { configuration } from './configuration.js';
import type { LectureFaite } from './confirmation_lue.js';
import { adresseDuFichier } from './rangement.js';
import type { Bon, LigneSolde, MouvementEmballage } from './emballage.js';
import type { Affectation, PlanRangement } from './affectation.js';
import type { Choix } from './annuaire.js';
import type { OrdreAFacturer, PieceListee } from './facturation.js';
import type {
  Acompte as AcompteClient,
  Encaissement as EncaissementListe,
  Gel as GelClient,
  PieceRecouvrement,
} from './recouvrement.js';
import type { Indisponibilite, Motif } from './indisponibilite.js';
import type { FicheOrdre, MotifLitige, OrdreTransport } from './ot.js';
import type { Planning } from './planning.js';
import type { ClientARattacher, ClientPennylane } from './rattachement.js';
import type { ResultatLu } from './resultat.js';

/**
 * Un échange avec l'API, une fois la réponse lue.
 *
 * `reseau` couvre trois cas que la personne devant l'écran ne distingue pas :
 * connexion coupée, API hors service, appel bloqué par le partage entre
 * origines. Dans les trois, on n'a rien reçu.
 */
export type Echange =
  | { readonly etat: 'reseau' }
  /** Réponse acceptée, avec un corps lisible. */
  | { readonly etat: 'succes'; readonly corps: unknown }
  /**
   * Réponse acceptée, SANS corps lisible. C'est la forme d'un `204` — la seule
   * réponse de l'API qui n'a rien à dire. Elle est distinguée de `reseau` : une
   * suppression réussie et une panne de réseau ne doivent surtout pas se
   * ressembler, sans quoi une coupure ferait disparaître une fiche de l'écran
   * alors qu'elle est toujours en base.
   */
  | { readonly etat: 'vide' }
  | {
      readonly etat: 'refus';
      readonly statut: number;
      readonly code: string | undefined;
      /**
       * LE CORPS DU REFUS, gardé depuis le 07/09/2026 pour l'import CSV.
       *
       * Presque tous les refus de l'API tiennent dans leur code : « introuvable »
       * n'a rien à ajouter. Celui de l'import, si — il porte le RAPPORT, c'est-à-dire
       * quelles lignes corriger. Sans ce corps, l'écran ne pourrait afficher que
       * « le fichier comporte des fautes », ce qui n'aide personne devant deux
       * cent soixante lignes.
       */
      readonly corps: unknown;
    };

interface CorpsRefus {
  readonly code?: unknown;
  /**
   * LA PHRASE DE L'API, gardée depuis le 15/09/2026.
   *
   * Elle existait déjà dans le corps de tout refus — « du : obligatoire »,
   * « l'indexation se pose entière » — et AUCUN écran ne la lisait : `echec()`
   * la jetait pour ne garder qu'un code, que chaque écran retraduisait par une
   * phrase de son cru.
   *
   * CE QUE ÇA A COÛTÉ : Mohamed, le 15/09, devant une fenêtre de neuf champs,
   * a lu « La saisie est incomplète ou hors des valeurs attendues » — vrai,
   * inutile, et incapable de dire QUEL champ. L'API, elle, le disait.
   */
  readonly erreur?: unknown;
}

/**
 * EXPORTÉE au lot 96 (18/09/2026) pour `apiAnalyse.ts` : le module Analyse
 * range ses appels à part plutôt que d'allonger ce fichier.
 */
export async function appeler(
  chemin: string,
  jeton: string,
  ecriture?: {
    readonly methode: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    readonly corps?: unknown;
  },
): Promise<Echange> {
  const entetes: Record<string, string> = { Authorization: `Bearer ${jeton}` };
  const init: RequestInit = { headers: entetes };

  if (ecriture !== undefined) {
    init.method = ecriture.methode;
    if (ecriture.corps !== undefined) {
      entetes['content-type'] = 'application/json';
      init.body = JSON.stringify(ecriture.corps);
    }
  }

  let reponse: Response;
  try {
    reponse = await fetch(`${configuration.apiUrl}${chemin}`, init);
  } catch {
    return { etat: 'reseau' };
  }

  let corps: unknown;
  let lisible = true;
  try {
    corps = await reponse.json();
  } catch {
    lisible = false;
  }

  if (reponse.ok) {
    return lisible ? { etat: 'succes', corps } : { etat: 'vide' };
  }

  const code = (corps as CorpsRefus | undefined)?.code;

  /**
   * ON ÉCRIT LE REFUS DANS LA CONSOLE, méthode, chemin et statut.
   *
   * Ajouté le 10/09/2026 : « quand je confirme supprimer une facture, le
   * message dit que Stafa ne répond pas ». Il fallait ouvrir F12 et lire
   * l'onglet Réseau pour savoir de quoi il s'agissait. Maintenant la Console
   * le dit en une ligne.
   *
   * NI LE JETON NI LE CORPS n'y figurent : le jeton est un secret, et le corps
   * peut porter des données client. Le statut et le code suffisent à nommer le
   * défaut.
   */
  if (typeof console !== 'undefined') {
    console.warn(
      `[Stafa] refus ${String(reponse.status)}${
        typeof code === 'string' ? ` (${code})` : ' (sans code)'
      } sur ${ecriture?.methode ?? 'GET'} ${chemin}`,
    );
  }

  return {
    etat: 'refus',
    statut: reponse.status,
    code: typeof code === 'string' ? code : undefined,
    corps,
  };
}

// ---------------------------------------------------------------------------
// /moi — le contrôle d'accès
// ---------------------------------------------------------------------------

export interface Moi {
  readonly utilisateur: {
    readonly id: string;
    readonly nom: string;
    readonly role: string;
  };
  readonly societes: readonly {
    readonly id: string;
    readonly raison_sociale: string;
  }[];
}

/** Ce que l'API peut refuser, une fois le jeton obtenu. */
export type EchecAcces = 'ferme' | 'desactive' | 'injoignable';

export type ResultatMoi =
  | { readonly etat: 'ok'; readonly moi: Moi }
  | { readonly etat: 'echec'; readonly cause: EchecAcces };

export async function lireMoi(jeton: string): Promise<ResultatMoi> {
  const echange = await appeler('/moi', jeton);

  if (echange.etat === 'succes') {
    return { etat: 'ok', moi: echange.corps as Moi };
  }
  if (echange.etat === 'reseau' || echange.etat === 'vide') {
    // Une réponse annoncée bonne mais illisible n'est pas exploitable : pour
    // `/moi`, c'est une panne — il n'y a rien à en tirer.
    return { etat: 'echec', cause: 'injoignable' };
  }
  if (echange.code === 'compte_desactive') {
    return { etat: 'echec', cause: 'desactive' };
  }
  if (echange.statut === 401 || echange.statut === 403) {
    // Jeton refusé, société absente, utilisateur inconnu de cette société :
    // pour la personne devant l'écran, c'est le même fait — l'accès n'est pas
    // ouvert. Le détail reste dans le code, lisible en cas de dépannage.
    return { etat: 'echec', cause: 'ferme' };
  }
  return { etat: 'echec', cause: 'injoignable' };
}

// ---------------------------------------------------------------------------
// Les annuaires — clients, transporteurs, et les suivants
// ---------------------------------------------------------------------------

/**
 * Ce qu'une route d'annuaire a besoin de savoir de son annuaire, et rien de
 * plus : le segment d'URL et les deux clés du corps de réponse.
 *
 * Déclaré ici plutôt qu'importé d'`annuaire.ts` pour que les deux fichiers ne
 * s'importent pas l'un l'autre. `Annuaire` porte ces trois champs, donc un
 * annuaire complet passe partout où celui-ci est attendu.
 */
export interface CheminAnnuaire {
  readonly chemin: string;
  readonly cleListe: string;
  readonly cleFiche: string;
}

/** Une ligne de la liste : seulement ce que le tableau affiche. */
export interface LigneListe {
  readonly id: string;
  readonly nom: string;
  readonly code: string | null;
  readonly ville: string | null;
  readonly code_postal: string | null;
  readonly telephone: string | null;
  readonly courriel: string | null;
  readonly actif: boolean;
}

/**
 * La fiche : les colonnes que la liste ne transporte pas, en plus.
 *
 * L'index est ouvert parce que les colonnes ne sont PAS les mêmes d'un annuaire
 * à l'autre — un transporteur porte `source`, un client non. Les champs se
 * lisent par `valeur(fiche, cle)` (voir `annuaire.ts`), qui rend `null` pour ce
 * qui manque : une colonne absente s'affiche « non renseigné » au lieu de faire
 * tomber l'écran.
 */
export interface FicheDetail extends LigneListe {
  readonly cree_le: string;
  readonly modifie_le: string;
  readonly [cle: string]: string | boolean | null;
}

export interface Contact {
  readonly id: string;
  readonly nom: string;
  readonly fonction: string | null;
  readonly telephone: string | null;
  /** Le second numéro — le portable, en pratique. Ajouté le 03/09/2026. */
  readonly mobile: string | null;
  readonly courriel: string | null;
  readonly note: string | null;
  /** Le contact à appeler en premier. Au plus un par fiche, tenu par la base. */
  readonly principal: boolean;
}

/**
 * Ce que l'écran reçoit d'une fiche, quelle que soit sa nature.
 *
 * **La clé du corps est retirée ici, et nulle part ailleurs** : l'API rend
 * `{ "client": … }` ou `{ "transporteur": … }`, l'écran ne connaît que `fiche`.
 * Sans cette normalisation, le nom de la clé remonterait jusque dans le rendu
 * et chaque composant devrait savoir de quel annuaire il dessine la fiche.
 */
/** Un matériel du référentiel commun : « T6 », « Semi tautliner ». */
export interface Vehicule {
  readonly code: string;
  readonly carrosserie: string;
  readonly carrosserie_nom: string;
  readonly gamme: number;
  readonly gamme_nom: string;
  readonly libelle: string;
}

/**
 * Une zone d'affrètement du référentiel commun : `FR45`, `ES-NAT`.
 *
 * Convention `[PAYS] + 2 chiffres du CP` de la compétence
 * `affretement-recherche-transporteur`. `pays` est détaché du code : c'est lui
 * qui permet de grouper la liste sans découper des chaînes.
 */
export interface Zone {
  readonly code: string;
  readonly pays: string;
  readonly nom: string;
}

/**
 * Une zone DESSERVIE par un transporteur, et dans quel sens.
 *
 * Distincte de `Zone` : celle-ci est une case cochée sur une fiche, celle-là
 * un point du référentiel. Les confondre — ce qu'a fait une première rédaction
 * du 06/09/2026 — donne deux types du même nom.
 */
export interface Couverture {
  readonly zone: string;
  readonly depart: boolean;
  readonly arrivee: boolean;
}

export interface Dossier {
  readonly fiche: FicheDetail;
  readonly contacts: readonly Contact[];
  /**
   * La couverture — ce que ce transporteur possède et ce qu'il dessert.
   *
   * ABSENTES sur une fiche de client, et non vides : l'API ne les rend que
   * pour un annuaire qui en porte une. Une liste vide dirait « ce client ne
   * dessert rien », ce qui n'a pas de sens pour lui, et l'écran n'afficherait
   * pas l'onglet de la même façon.
   */
  readonly vehicules?: readonly string[];
  readonly zones?: readonly Couverture[];
}

/** Retire la clé de l'annuaire du corps rendu par l'API. */
function normaliser(corps: unknown, annuaire: CheminAnnuaire): Dossier {
  const brut = corps as Record<string, unknown>;
  const dossier: {
    fiche: FicheDetail;
    contacts: readonly Contact[];
    vehicules?: readonly string[];
    zones?: readonly Couverture[];
  } = {
    fiche: brut[annuaire.cleFiche] as FicheDetail,
    contacts: (brut['contacts'] ?? []) as readonly Contact[],
  };

  // Recopiées seulement si l'API les a rendues : l'absence est une
  // information, elle dit que cet annuaire ne porte pas de couverture.
  if (Array.isArray(brut['vehicules'])) {
    dossier.vehicules = brut['vehicules'] as readonly string[];
  }
  if (Array.isArray(brut['zones'])) {
    dossier.zones = brut['zones'] as readonly Couverture[];
  }
  return dossier;
}

/**
 * Ce qui peut mal tourner sur un écran métier, du point de vue de la personne.
 *
 *   - `acces` : la séance ne vaut plus. L'écran ne bricole pas un message, il
 *     rend la main à l'aiguillage, qui renverra à la connexion ;
 *   - `introuvable` : la fiche demandée n'existe pas (ou plus — quelqu'un a pu
 *     la supprimer entre l'affichage de la liste et le clic) ;
 *   - `injoignable` : on n'a rien reçu, ou l'API a répondu par une panne ;
 *   - `code_pris` : une autre fiche de la société porte déjà ce code. C'est la
 *     base qui l'a refusé, par son index `(societe_id, code)` ; l'écran ne fait
 *     que le mettre en mots ;
 *   - `invalide` : l'API a refusé le corps. Ne devrait pas arriver — l'écran
 *     contrôle avant d'envoyer — mais ce filet dit qu'il est en retard sur
 *     l'API plutôt que de laisser croire à une panne ;
 *   - `encore_actif` : on a demandé de supprimer une fiche encore active. On
 *     désactive d'abord ;
 *   - `encore_rattache` : des lignes s'y rattachent encore. C'est la BASE qui
 *     refuse, par ses clés étrangères `on delete restrict` ; l'API met le refus
 *     en mots et l'écran le montre. Aucun des trois ne le remplace ;
 *   - `conflit` : deux périodes se recouvrent sur la même ressource. C'est la
 *     contrainte d'exclusion de la base, que l'API rend sous
 *     `periode_en_conflit`.
 *
 * **`conflit` a été AJOUTÉ le 04/09/2026, après un défaut constaté sur le
 * poste.** Il manquait : `periode_en_conflit` n'était traduit nulle part et
 * tombait donc dans le cas par défaut de `echec()`, c'est-à-dire `injoignable`.
 * Un refus métier parfaitement normal s'annonçait « Stafa ne répond pas,
 * vérifiez votre connexion » — de quoi faire recharger la page à quelqu'un qui
 * devait simplement décrocher d'abord. En face, l'écran Affectation compensait
 * en affichant le message de conflit pour TOUT `invalide`, si bien qu'une fin
 * antérieure au début — le cas réellement rencontré — s'annonçait comme un
 * recouvrement. Les deux messages étaient faux, chacun dans un sens.
 */
export type EchecApi =
  | 'acces'
  | 'introuvable'
  | 'injoignable'
  | 'code_pris'
  | 'invalide'
  | 'conflit'
  | 'encore_actif'
  | 'encore_rattache'
  // Les CINQ refus du planning, ajoutés le 04/09/2026. Chacun se dit à l'écran
  // par une phrase : « données invalides » devant un camion qu'on vient de
  // poser ne dirait rien à personne.
  | 'ordre_annule'
  | 'ordre_sans_etape'
  | 'etape_validee'
  | 'jour_sans_etape'
  | 'etape_en_tournee'
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. La
  // réparation n'est pas au même endroit : `journee_cloturee` se lève ICI,
  // `journee_sans_chainage` dans une AUTRE journée — la précédente de ce
  // camion. Un code unique enverrait la moitié des gens au mauvais écran.
  | 'journee_cloturee'
  | 'journee_sans_chainage'
  // LES DEUX REFUS DU LITIGE, ajoutés le 07/09/2026. Ce ne sont pas des
  // saisies fautives : c'est l'ÉTAT de l'étape ou du litige qui s'y oppose, et
  // l'écran doit pouvoir proposer la suite plutôt qu'afficher « invalide ».
  | 'litige_deja_ouvert'
  | 'litige_deja_resolu'
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Même raison que
  // ci-dessus : ce sont des ÉTATS, pas des saisies fautives. « Cette pièce est
  // émise » se répare par un avoir, « ce litige bloque » se répare en réglant
  // le litige — deux suites différentes que « invalide » ne dirait pas.
  | 'litige_ouvert'
  | 'deja_facture'
  | 'rien_a_facturer'
  | 'facture_emise'
  | 'facture_brouillon'
  | 'facture_vide'
  // AJOUTÉ LE 09/09/2026. Même famille : c'est un ÉTAT qui refuse, pas une
  // saisie fautive. Et celui-ci porte sa propre suite — « Retirer de la
  // facturation » — que l'écran doit pouvoir proposer.
  | 'ordre_en_facturation'
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Même famille encore :
  // des ÉTATS, pas des saisies fautives, et chacun porte sa propre suite —
  // « corrigez la ventilation », « levez le gel en cours », « validez d'abord
  // ce brouillon ». « Invalide » ne dirait aucune des trois.
  | 'affectation_excessive'
  | 'piece_non_emise'
  | 'piece_autre_client'
  | 'gel_deja_actif'
  | 'gel_absent'
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  // Chacun porte sa suite : « corrigez par un avoir », « détachez le
  // règlement d'abord ».
  | 'numero_pas_le_dernier'
  | 'facture_reglee'
  /**
   * UNE RELANCE RETIENT LA PIÈCE — ajouté le 10/09/2026 sur un défaut constaté
   * EN PRODUCTION. La base refusait déjà (`relance_pieces` pointe sur
   * `factures` en `on delete restrict`), mais son refus sortait en 500 et
   * l'écran parlait d'une panne. Le refus a maintenant un nom et une phrase.
   */
  | 'piece_relancee'
  | 'facture_sans_echeance'
  /**
   * CE CLIENT PENNYLANE EST DÉJÀ RATTACHÉ À UNE AUTRE FICHE — ajouté le
   * 11/09/2026 avec l'écran de rattachement.
   *
   * Il MÉRITE son code plutôt que de se confondre avec `code_pris` : le geste
   * de réparation n'est pas le même. « Code déjà pris » ferait chercher un
   * doublon de code client ; ici le cas probable est un DOUBLON DE FICHE dans
   * l'annuaire, que le rattachement vient justement de révéler.
   */
  | 'pennylane_deja_rattache'
  /**
   * LES DEUX REFUS DE L'ENVOI CHEZ PENNYLANE — 11/09/2026. Deux et pas un :
   * le premier se répare dans UN AUTRE ÉCRAN (Paramètres → Pennylane), le
   * second en corrigeant la facture — ou pas du tout, quand c'est Pennylane
   * qui refuse. L'écran ne propose pas la même suite.
   */
  | 'pennylane_client_non_rattache'
  | 'pennylane_impossible'
  /**
   * LE DÉPÔT D'UNE ANNEXE SUR UNE FACTURE QUI N'EST PAS ENCORE CHEZ EUX —
   * 14/09/2026. Un code à part parce que la suite est différente des deux
   * précédents : envoyer la facture d'abord, ni la corriger ni rattacher un
   * client. En usage normal il ne sort pas — l'écran dépose dans la foulée de
   * l'envoi — mais deux onglets sur la même facture suffisent à le produire.
   */
  | 'facture_pas_deposee'
  /**
   * LA FACTURE A CHANGÉ DEPUIS L'APERÇU — 14/09/2026. Un code à part parce que
   * la suite est unique : redemander l'aperçu, puis valider. Il ferme le défaut
   * qui a fait finaliser une pièce à l'ancienne date, et la finalisation ne se
   * défait pas.
   */
  | 'facture_modifiee_depuis_apercu'
  /**
   * LES QUATRE REFUS DE L'AVOIR — 11/09/2026.
   *
   * Quatre et pas un : la SUITE diffère à chaque fois, et c'est tout l'objet
   * d'un code. « Cette facture est déjà avoirée » mène à l'avoir existant ;
   * « cet avoir dépasse » mène à corriger un montant ; « on n'avoire pas un
   * avoir » ne mène nulle part, il n'y a rien à réparer.
   *
   * ILS SONT DÉCLARÉS AVANT QUE L'ÉCRAN N'EXISTE, délibérément. Un code que
   * l'interface ne connaît pas tombe dans `service_en_erreur`, dont la phrase
   * parle de panne — exactement le mensonge constaté le 10/09, où l'on faisait
   * vérifier son wifi à quelqu'un dont la facture était simplement refusée.
   */
  | 'avoir_sur_avoir'
  | 'facture_deja_avoiree'
  | 'avoir_trop_grand'
  | 'avoir_impossible'
  /**
   * LE SERVICE A RÉPONDU, MAIS AVEC UNE ERREUR QU'ON NE SAIT PAS NOMMER —
   * ajouté le 10/09/2026 sur un défaut signalé par l'utilisateur : la
   * suppression d'une facture affichait « Stafa ne répond pas, vérifiez votre
   * connexion ».
   *
   * C'était un MENSONGE. Tout refus non reconnu — un 500, un 501 sur une route
   * que le Worker déployé ne connaît pas encore, un 404 sans code — tombait
   * dans `injoignable`, dont la phrase parle de connexion. La personne
   * vérifiait son wifi pendant que le vrai problème était ailleurs.
   *
   * Désormais `injoignable` ne dit plus que ce qu'il sait : RIEN N'EST REVENU.
   * Ce code-ci dit l'inverse : quelque chose est revenu, et ce n'est pas la
   * faute du réseau.
   */
  | 'service';

export type Resultat<T> =
  | { readonly etat: 'ok'; readonly valeur: T }
  | {
      readonly etat: 'echec';
      readonly cause: EchecApi;
      /**
       * CE QUE L'API A DIT, mot pour mot, quand elle l'a dit — 15/09/2026.
       *
       * FACULTATIF, et c'est voulu : les écrans qui ne la lisent pas gardent
       * leur comportement au caractère près. Ceux qui la lisent gagnent un
       * message qui nomme le champ fautif au lieu d'une formule générale.
       *
       * Un écran qui l'affiche doit garder son repli : une panne de réseau
       * n'a pas de phrase d'API, et « undefined » ne se montre pas.
       */
      readonly message?: string;
    };

/**
 * La phrase du refus, si l'API en a donné une.
 *
 * `corps` est `unknown` : il vient du réseau, et rien ne garantit sa forme.
 * On ne rend une chaîne que si c'en est vraiment une, non vide et pas
 * démesurée — un corps d'erreur de mille caractères tiendrait mal dans un
 * bandeau.
 */
function phraseDuRefus(echange: Echange): string | undefined {
  if (echange.etat !== 'refus') return undefined;
  const corps = echange.corps as CorpsRefus | null | undefined;
  const dite = corps?.erreur;
  if (typeof dite !== 'string') return undefined;
  const propre = dite.trim();
  if (propre === '' || propre.length > 300) return undefined;
  return propre;
}

/**
 * Traduit un échange raté. Un échange réussi ne passe jamais par ici.
 *
 * DEUX FONCTIONS PLUTÔT QU'UNE depuis le 15/09/2026 : `causeDuRefus` garde les
 * dix-neuf traductions de code, inchangées, et `echec` y ajoute la phrase de
 * l'API. Les toucher toutes pour glisser un champ aurait été dix-neuf
 * occasions de se tromper.
 */
/** Exportée au lot 96, avec `appeler`. */
export function echec(echange: Echange): {
  etat: 'echec';
  cause: EchecApi;
  message?: string;
} {
  const cause = causeDuRefus(echange);
  const phrase = phraseDuRefus(echange);
  return phrase === undefined ? cause : { ...cause, message: phrase };
}

function causeDuRefus(echange: Echange): { etat: 'echec'; cause: EchecApi } {
  if (echange.etat === 'refus') {
    if (echange.code === 'introuvable') {
      return { etat: 'echec', cause: 'introuvable' };
    }
    if (echange.code === 'code_deja_pris') {
      return { etat: 'echec', cause: 'code_pris' };
    }
    if (echange.code === 'donnees_invalides') {
      return { etat: 'echec', cause: 'invalide' };
    }
    if (echange.code === 'periode_en_conflit') {
      return { etat: 'echec', cause: 'conflit' };
    }
    if (echange.code === 'encore_actif') {
      return { etat: 'echec', cause: 'encore_actif' };
    }
    if (echange.code === 'encore_rattache') {
      return { etat: 'echec', cause: 'encore_rattache' };
    }
    if (
      echange.code === 'ordre_annule' ||
      echange.code === 'ordre_sans_etape' ||
      echange.code === 'etape_validee' ||
      echange.code === 'jour_sans_etape' ||
      echange.code === 'etape_en_tournee' ||
      echange.code === 'journee_cloturee' ||
      echange.code === 'journee_sans_chainage' ||
      echange.code === 'litige_deja_ouvert' ||
      echange.code === 'litige_deja_resolu' ||
      echange.code === 'litige_ouvert' ||
      echange.code === 'deja_facture' ||
      echange.code === 'rien_a_facturer' ||
      echange.code === 'facture_emise' ||
      echange.code === 'facture_brouillon' ||
      echange.code === 'facture_vide' ||
      echange.code === 'ordre_en_facturation' ||
      echange.code === 'affectation_excessive' ||
      echange.code === 'piece_non_emise' ||
      echange.code === 'piece_autre_client' ||
      echange.code === 'gel_deja_actif' ||
      echange.code === 'gel_absent' ||
      echange.code === 'numero_pas_le_dernier' ||
      echange.code === 'facture_reglee' ||
      echange.code === 'piece_relancee' ||
      echange.code === 'facture_sans_echeance' ||
      echange.code === 'pennylane_deja_rattache' ||
      echange.code === 'pennylane_client_non_rattache' ||
      echange.code === 'pennylane_impossible' ||
      echange.code === 'facture_pas_deposee' ||
      echange.code === 'facture_modifiee_depuis_apercu'
    ) {
      return { etat: 'echec', cause: echange.code };
    }
    if (echange.statut === 401 || echange.statut === 403) {
      return { etat: 'echec', cause: 'acces' };
    }
    // ON A REÇU QUELQUE CHOSE, et on ne sait pas quoi. Ce n'est pas la
    // connexion : le dire évite de chercher du côté du wifi.
    return { etat: 'echec', cause: 'service' };
  }
  return { etat: 'echec', cause: 'injoignable' };
}

export async function lireListe(
  jeton: string,
  annuaire: CheminAnnuaire,
): Promise<Resultat<readonly LigneListe[]>> {
  const echange = await appeler(`/${annuaire.chemin}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as Record<string, readonly LigneListe[] | undefined>;
  return { etat: 'ok', valeur: corps[annuaire.cleListe] ?? [] };
}

export async function lireFiche(
  jeton: string,
  annuaire: CheminAnnuaire,
  id: string,
): Promise<Resultat<Dossier>> {
  const echange = await appeler(`/${annuaire.chemin}/${encodeURIComponent(id)}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

/**
 * Ce qu'une fiche accepte comme champs écrits.
 *
 * Ouvert, parce que les champs viennent du descripteur de l'annuaire et ne sont
 * pas les mêmes des deux côtés. C'est l'API qui ferme : `refuserLesInconnus`
 * refuse toute clé qu'elle ne connaît pas. `actif` n'est pas envoyé par le
 * formulaire — il se change par les boutons Désactiver et Réactiver.
 */
export type ChampsFiche = Readonly<Record<string, string | null>>;

export async function creerFiche(
  jeton: string,
  annuaire: CheminAnnuaire,
  champs: ChampsFiche,
): Promise<Resultat<Dossier>> {
  const echange = await appeler(`/${annuaire.chemin}`, jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

/**
 * Modification partielle : on n'envoie que ce qui a changé. Un champ absent
 * n'est pas touché, un champ à `null` est effacé — c'est le contrat de l'API,
 * et c'est aussi ce qui rend le journal des traces lisible : il ne garde que
 * les valeurs réellement modifiées.
 */
export async function modifierFiche(
  jeton: string,
  annuaire: CheminAnnuaire,
  id: string,
  champs: ChampsFiche,
): Promise<Resultat<Dossier>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(id)}`,
    jeton,
    {
      methode: 'PATCH',
      corps: champs,
    },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

// ---------------------------------------------------------------------------
// Les contacts — tous sous leur fiche
// ---------------------------------------------------------------------------

/**
 * Ce qu'un contact accepte comme champs écrits. Miroir de `CHAMPS_CONTACT` dans
 * `apps/api/src/contacts.ts`. Ni le rattachement ni `societe_id` : le premier
 * vient du chemin, le second du jeton.
 */
export interface ChampsContact {
  readonly nom?: string | null;
  readonly fonction?: string | null;
  readonly telephone?: string | null;
  readonly mobile?: string | null;
  readonly courriel?: string | null;
  readonly note?: string | null;
  readonly principal?: boolean;
}

/**
 * Les trois routes rendent LA FICHE COMPLÈTE, comme `GET /clients/:id` — y
 * compris la suppression, qui rend 200 avec un corps là où la suppression d'une
 * fiche rend 204. C'est voulu : la fiche existe encore. L'écran n'a donc qu'une
 * seule forme à traiter et ne recharge jamais.
 */
export async function creerContact(
  jeton: string,
  annuaire: CheminAnnuaire,
  ficheId: string,
  champs: ChampsContact,
): Promise<Resultat<Dossier>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(ficheId)}/contacts`,
    jeton,
    { methode: 'POST', corps: champs },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

export async function modifierContact(
  jeton: string,
  annuaire: CheminAnnuaire,
  ficheId: string,
  contactId: string,
  champs: ChampsContact,
): Promise<Resultat<Dossier>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(ficheId)}/contacts/${encodeURIComponent(contactId)}`,
    jeton,
    { methode: 'PATCH', corps: champs },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

export async function supprimerContact(
  jeton: string,
  annuaire: CheminAnnuaire,
  ficheId: string,
  contactId: string,
): Promise<Resultat<Dossier>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(ficheId)}/contacts/${encodeURIComponent(contactId)}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

/**
 * Désactiver ou réactiver une fiche.
 *
 * Passe par la même route que la modification, avec `{ "actif": … }` : une
 * fiche désactivée n'est pas un autre genre d'objet et ne mérite pas une route
 * à part. Le formulaire, lui, ne touche jamais à `actif` — c'est ce bouton, et
 * lui seul.
 */
export async function changerActivite(
  jeton: string,
  annuaire: CheminAnnuaire,
  id: string,
  actif: boolean,
): Promise<Resultat<Dossier>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(id)}`,
    jeton,
    {
      methode: 'PATCH',
      corps: { actif },
    },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

/**
 * Supprimer une fiche. Ne rend RIEN : elle n'existe plus, il n'y a pas de corps
 * à renvoyer — c'est le seul `DELETE` de l'API dans ce cas, celui d'un contact
 * rendant la fiche à laquelle il était rattaché, qui, elle, existe encore.
 *
 * La règle arrêtée le 03/09/2026 : on désactive, et on ne supprime que si plus
 * rien ne s'y rattache. La seconde moitié est tenue par la base — `on delete
 * restrict` sur la colonne de rattachement des contacts, et il en ira de même
 * des transports et des factures.
 */
export async function supprimerFiche(
  jeton: string,
  annuaire: CheminAnnuaire,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(id)}`,
    jeton,
    {
      methode: 'DELETE',
    },
  );

  // `vide` est la réponse ATTENDUE ici : un 204 n'a pas de corps. C'est le seul
  // endroit de l'interface où une réponse sans corps est la bonne réponse — et
  // elle ne se confond pas avec `reseau`, qui reste une panne.
  if (echange.etat === 'vide' || echange.etat === 'succes') {
    return { etat: 'ok', valeur: null };
  }
  return echec(echange);
}

// ---------------------------------------------------------------------------
// Les référentiels communs, et la couverture d'un transporteur
// ---------------------------------------------------------------------------

/**
 * Les 24 matériels, dans l'ordre du référentiel. Chargés une fois, à
 * l'ouverture de l'onglet : ils ne changent pas d'une fiche à l'autre, et une
 * migration seule peut les faire bouger.
 */
export async function lireVehicules(
  jeton: string,
): Promise<Resultat<readonly Vehicule[]>> {
  const echange = await appeler('/vehicules', jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { vehicules?: readonly Vehicule[] };
  return { etat: 'ok', valeur: corps.vehicules ?? [] };
}

/**
 * Les zones d'affrètement, raccourcis nationaux en tête.
 *
 * Remplace `lireDepartements` le 06/09/2026 : cocher « France entière »
 * devait rester UN geste, et « ES28 » devait pouvoir se dire.
 */
/** Un pays et son code ISO, tels que la base les range. */
export interface Pays {
  /** En minuscules et sans accent — la forme rangée de la table. */
  readonly nom: string;
  readonly iso: string;
}

/**
 * La traduction « France » → « FR », lue en base et NON recopiée ici.
 *
 * L'API s'en sert pour figer l'axe d'un affrètement, l'écran pour le proposer.
 * Deux listes qui divergent dirigeraient un affrètement vers le mauvais pays.
 */
export async function lirePays(jeton: string): Promise<Resultat<readonly Pays[]>> {
  const echange = await appeler('/pays', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { pays?: readonly Pays[] };
  return { etat: 'ok', valeur: corps.pays ?? [] };
}

export async function lireZones(jeton: string): Promise<Resultat<readonly Zone[]>> {
  const echange = await appeler('/zones', jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { zones?: readonly Zone[] };
  return { etat: 'ok', valeur: corps.zones ?? [] };
}

/**
 * Enregistre le matériel coché. REMPLACEMENT : la liste envoyée devient la
 * liste, et une liste vide décoche tout. Rend la fiche complète, comme les
 * routes des contacts — l'écran n'a rien à recharger.
 */
export async function enregistrerVehicules(
  jeton: string,
  annuaire: CheminAnnuaire,
  ficheId: string,
  codes: readonly string[],
): Promise<Resultat<Dossier>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(ficheId)}/vehicules`,
    jeton,
    { methode: 'PUT', corps: { codes } },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

/** Même règle pour les zones desservies. */
export async function enregistrerZones(
  jeton: string,
  annuaire: CheminAnnuaire,
  ficheId: string,
  zones: readonly Couverture[],
): Promise<Resultat<Dossier>> {
  const echange = await appeler(
    `/${annuaire.chemin}/${encodeURIComponent(ficheId)}/zones`,
    jeton,
    { methode: 'PUT', corps: { zones } },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: normaliser(echange.corps, annuaire) };
}

// ---------------------------------------------------------------------------
// Le parc : véhicules, remorques, chauffeurs
// ---------------------------------------------------------------------------

/**
 * Les appels du parc.
 *
 * **Une seule forme rendue.** La liste et la fiche rendent les mêmes colonnes,
 * contrairement aux annuaires où une liste de plusieurs centaines de clients ne
 * transporte que huit colonnes sur vingt. C'est le choix fait côté API : un
 * parc tient en quelques dizaines de lignes de cinq champs, et deux formes à
 * tenir en accord coûteraient plus que ce qu'elles feraient gagner.
 *
 * D'où l'absence de `normaliser` ici : il n'y a rien à recomposer, seulement
 * une clé à retirer.
 */
export interface CheminParc {
  /** « chauffeurs », « parc/vehicules » — DEUX segments sont permis. */
  readonly chemin: string;
  readonly cleListe: string;
  readonly cleFiche: string;
}

/** Ce qu'une ligne de parc peut porter. `charge_utile` est un entier. */
type LigneBrute = Readonly<Record<string, string | number | boolean | null>> & {
  readonly id: string;
  readonly actif: boolean;
};

export async function lireParc(
  jeton: string,
  parc: CheminParc,
): Promise<Resultat<readonly LigneBrute[]>> {
  const echange = await appeler(`/${parc.chemin}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as Record<string, readonly LigneBrute[] | undefined>;
  return { etat: 'ok', valeur: corps[parc.cleListe] ?? [] };
}

/** Retire la clé du corps rendu — « {"remorque": {…}} » devient la ligne. */
function ligneDe(corps: unknown, parc: CheminParc): LigneBrute {
  const enveloppe = corps as Record<string, LigneBrute | undefined>;
  const ligne = enveloppe[parc.cleFiche];
  if (ligne === undefined) {
    throw new Error(`corps sans « ${parc.cleFiche} »`);
  }
  return ligne;
}

export async function lireLigneParc(
  jeton: string,
  parc: CheminParc,
  id: string,
): Promise<Resultat<LigneBrute>> {
  const echange = await appeler(`/${parc.chemin}/${encodeURIComponent(id)}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: ligneDe(echange.corps, parc) };
}

export async function creerLigneParc(
  jeton: string,
  parc: CheminParc,
  champs: ChampsFiche,
): Promise<Resultat<LigneBrute>> {
  const echange = await appeler(`/${parc.chemin}`, jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: ligneDe(echange.corps, parc) };
}

/** Modification partielle — même contrat que l'annuaire : `null` efface. */
export async function modifierLigneParc(
  jeton: string,
  parc: CheminParc,
  id: string,
  champs: ChampsFiche,
): Promise<Resultat<LigneBrute>> {
  const echange = await appeler(`/${parc.chemin}/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: ligneDe(echange.corps, parc) };
}

/** Désactiver ou réactiver. Même route que la modification, comme l'annuaire. */
export async function changerActiviteParc(
  jeton: string,
  parc: CheminParc,
  id: string,
  actif: boolean,
): Promise<Resultat<LigneBrute>> {
  const echange = await appeler(`/${parc.chemin}/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: { actif },
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: ligneDe(echange.corps, parc) };
}

/**
 * Supprimer. Rend 204, donc rien.
 *
 * La base refuse tant que la ligne est ATTELÉE — `on delete restrict` sur
 * `affectations`, éprouvé dans les tests de cloisonnement. L'écran traduit ce
 * refus en `encore_rattache`, comme pour une fiche d'annuaire qui porte encore
 * un contact.
 */
export async function supprimerLigneParc(
  jeton: string,
  parc: CheminParc,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/${parc.chemin}/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat === 'vide' || echange.etat === 'succes') {
    return { etat: 'ok', valeur: null };
  }
  return echec(echange);
}

// ---------------------------------------------------------------------------
// Les attelages et les arrêts
// ---------------------------------------------------------------------------

/**
 * L'état des attelages à une DATE, ou un historique.
 *
 * `le` demande l'état à cette date — les périodes qui la couvrent. C'est ce que
 * l'écran demande pour dessiner le parc. `du`/`au`/`vehicule`/`chauffeur`/
 * `remorque` servent la recherche d'historique ; une fenêtre garde ce qu'elle
 * CHEVAUCHE, non ce qui y tient entièrement — une période à cheval sur le
 * 1er avril concerne bien avril.
 *
 * Sans aucun filtre : tout, du plus récent au plus ancien, borné à 500 lignes
 * par l'API.
 */
export type FiltreAffectations = Readonly<
  Partial<Record<'le' | 'du' | 'au' | 'vehicule' | 'chauffeur' | 'remorque', string>>
>;

function chaineDeRequete(filtre: Readonly<Record<string, string | undefined>>): string {
  const parametres = new URLSearchParams();
  for (const [nom, valeur] of Object.entries(filtre)) {
    if (valeur !== undefined && valeur !== '') parametres.set(nom, valeur);
  }
  const rendu = parametres.toString();
  return rendu === '' ? '' : `?${rendu}`;
}

export async function lireAffectations(
  jeton: string,
  filtre: FiltreAffectations = {},
): Promise<Resultat<readonly Affectation[]>> {
  const echange = await appeler(`/affectations${chaineDeRequete(filtre)}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { affectations?: readonly Affectation[] };
  return { etat: 'ok', valeur: corps.affectations ?? [] };
}

export async function lireIndisponibilites(
  jeton: string,
): Promise<Resultat<readonly Indisponibilite[]>> {
  const echange = await appeler('/indisponibilites', jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { indisponibilites?: readonly Indisponibilite[] };
  return { etat: 'ok', valeur: corps.indisponibilites ?? [] };
}

/**
 * Atteler : une ligne porte EXACTEMENT une ressource, chauffeur ou remorque.
 * Poser les deux, ou aucune, est refusé — par l'API d'abord, par la base
 * ensuite (`num_nonnulls(...) = 1`).
 */
export interface Attelement {
  readonly vehicule_id: string;
  readonly chauffeur_id?: string;
  readonly remorque_id?: string;
  readonly du: string;
  readonly au?: string;
}

export async function atteler(
  jeton: string,
  attelement: Attelement,
): Promise<Resultat<Affectation>> {
  const echange = await appeler('/affectations', jeton, {
    methode: 'POST',
    corps: attelement,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { affectation?: Affectation };
  if (corps.affectation === undefined) {
    throw new Error('corps sans « affectation »');
  }
  return { etat: 'ok', valeur: corps.affectation };
}

/**
 * DÉTACHER, C'EST POSER LA FIN. Il n'y a pas de route « détacher » : un verbe
 * de plus dirait la même chose et laisserait croire qu'une période close
 * n'existe plus. La suppression existe pour une saisie fautive, et pour elle
 * seule.
 */
export async function changerAttelage(
  jeton: string,
  id: string,
  champs: Readonly<{ du?: string; au?: string | null; note?: string | null }>,
): Promise<Resultat<Affectation>> {
  const echange = await appeler(`/affectations/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { affectation?: Affectation };
  if (corps.affectation === undefined) {
    throw new Error('corps sans « affectation »');
  }
  return { etat: 'ok', valeur: corps.affectation };
}

export async function supprimerAttelage(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/affectations/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat === 'vide' || echange.etat === 'succes') {
    return { etat: 'ok', valeur: null };
  }
  return echec(echange);
}

/**
 * CHANGER UN ATTELAGE SANS BLOCAGE — lot 94.
 *
 * Le même appel sert l'APERÇU (`simuler: true`, rien n'est écrit) et
 * l'EXÉCUTION : l'API calcule le plan des deux fois avec le même code, si bien
 * que ce que la personne valide est ce qui s'écrit. Toutes les écritures
 * passent dans une seule transaction.
 *
 * Le corps se fabrique par `corpsDeChangement` (affectation.ts), ou, pour
 * corriger une période de l'historique, `{ remplace_id, du, au?, simuler }`.
 */
export async function rangerAttelages(
  jeton: string,
  corps: Readonly<Record<string, unknown>>,
): Promise<Resultat<PlanRangement>> {
  const echange = await appeler('/affectations/ranger', jeton, {
    methode: 'POST',
    corps,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const plan = echange.corps as Partial<PlanRangement>;
  if (!Array.isArray(plan.ecritures) || !Array.isArray(plan.avertissements)) {
    throw new Error('corps sans plan de rangement');
  }
  return { etat: 'ok', valeur: plan as PlanRangement };
}

/**
 * L'ÉCHANGE : quatre écritures en UNE SEULE transaction, tenue par
 * l'intercepteur de l'API. Si la quatrième échoue, les trois premières sont
 * annulées — la promesse « si l'une échoue, aucune ne passe » est donc vraie,
 * et l'écran n'a rien à défaire lui-même.
 *
 * Rend l'état des deux véhicules après coup : l'écran n'a pas à recharger.
 */
export async function echangerAttelages(
  jeton: string,
  ordre: Readonly<{
    vehicule_a: string;
    vehicule_b: string;
    quoi: 'chauffeurs' | 'remorques' | 'les_deux';
    du: string;
  }>,
): Promise<Resultat<readonly Affectation[]>> {
  const echange = await appeler('/affectations/echange', jeton, {
    methode: 'POST',
    corps: ordre,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { affectations?: readonly Affectation[] };
  return { etat: 'ok', valeur: corps.affectations ?? [] };
}

/**
 * Poser un arrêt. La ressource se donne sous SA SEULE colonne — chauffeur_id,
 * vehicule_id ou remorque_id — et exactement une : la base le tient par
 * `num_nonnulls(...) = 1`.
 *
 * **La famille n'est jamais envoyée** : l'API la déduit de la ressource. La
 * laisser saisir permettrait de la faire mentir, et c'est elle qui, dans la clé
 * à trois colonnes, garantit qu'un camion ne part pas en congés.
 */
export async function poserArret(
  jeton: string,
  champs: Readonly<Record<string, string>>,
): Promise<Resultat<Indisponibilite>> {
  const echange = await appeler('/indisponibilites', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { indisponibilite?: Indisponibilite };
  if (corps.indisponibilite === undefined) {
    throw new Error('corps sans « indisponibilite »');
  }
  return { etat: 'ok', valeur: corps.indisponibilite };
}

/**
 * Corriger un arrêt : les dates, le motif, la note.
 *
 * **La ressource ne se change pas** — déplacer une absence d'un chauffeur à un
 * autre n'est pas une correction, c'est une autre absence, et l'API refuse les
 * trois colonnes comme inconnues. `au` à `null` rouvre un arrêt clos par
 * erreur : c'est un ordre valide, et tout l'intérêt du champ.
 */
export async function changerArret(
  jeton: string,
  id: string,
  champs: Readonly<Record<string, string | null>>,
): Promise<Resultat<Indisponibilite>> {
  const echange = await appeler(`/indisponibilites/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { indisponibilite?: Indisponibilite };
  if (corps.indisponibilite === undefined) {
    throw new Error('corps sans « indisponibilite »');
  }
  return { etat: 'ok', valeur: corps.indisponibilite };
}

/** Supprimer un arrêt — pour une saisie fautive, et pour elle seule. */
export async function supprimerArret(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/indisponibilites/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat === 'vide' || echange.etat === 'succes') {
    return { etat: 'ok', valeur: null };
  }
  return echec(echange);
}

/**
 * Les motifs de la société — LES ACTIFS COMME LES INACTIFS.
 *
 * L'écran de paramétrage a besoin des deux ; le déroulant de saisie filtre
 * lui-même sur `actif`. Un motif désactivé reste lisible sur les arrêts passés,
 * et c'est pourquoi on désactive plutôt que de supprimer : l'historique dirait
 * sinon « immobilisé pour rien ».
 */
export async function lireMotifs(jeton: string): Promise<Resultat<readonly Motif[]>> {
  const echange = await appeler('/motifs-indisponibilite', jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { motifs?: readonly Motif[] };
  return { etat: 'ok', valeur: corps.motifs ?? [] };
}

/**
 * Créer un motif. La FAMILLE se pose ici et ne se modifiera plus : la faire
 * passer du matériel au personnel rendrait fausses les indisponibilités déjà
 * écrites. Un motif mal classé se désactive et se recrée.
 */
export async function creerMotif(
  jeton: string,
  nom: string,
  famille: 'personnel' | 'materiel',
): Promise<Resultat<Motif>> {
  const echange = await appeler('/motifs-indisponibilite', jeton, {
    methode: 'POST',
    corps: { nom, famille },
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { motif?: Motif };
  if (corps.motif === undefined) {
    throw new Error('corps sans « motif »');
  }
  return { etat: 'ok', valeur: corps.motif };
}

/** Renommer un motif, ou l'activer et le désactiver. Pas de suppression. */
export async function changerMotif(
  jeton: string,
  id: string,
  champs: Readonly<{ nom?: string; actif?: boolean }>,
): Promise<Resultat<Motif>> {
  const echange = await appeler(
    `/motifs-indisponibilite/${encodeURIComponent(id)}`,
    jeton,
    { methode: 'PATCH', corps: champs },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { motif?: Motif };
  if (corps.motif === undefined) {
    throw new Error('corps sans « motif »');
  }
  return { etat: 'ok', valeur: corps.motif };
}

// ---------------------------------------------------------------------------
// Les ordres de transport et leurs étapes
// ---------------------------------------------------------------------------

/**
 * La liste, BORNÉE PAR UNE FENÊTRE DE DATES.
 *
 * Contrairement aux annuaires, dont la liste entière tient en mémoire, le
 * nombre d'ordres grandit sans fin : l'API en rend 500 au plus, et sans fenêtre
 * la première réponse serait « les 500 derniers », qui n'est une réponse à
 * rien. `du` et `au` portent sur les DATES DES ÉTAPES — un ordre est retenu si
 * l'une des siennes tombe dedans.
 *
 * La recherche par texte, elle, reste LOCALE pour la frappe courante : elle
 * porte aussi sur les villes, que l'API ne cherche pas, et rappeler l'API à
 * chaque touche ferait clignoter la liste pour un calcul que le navigateur fait
 * en une milliseconde.
 *
 * `recherche` N'EST PAS CETTE FRAPPE, ajouté le 26/10/2026. C'est un GESTE
 * VOLONTAIRE — le bouton « chercher dans toute la base », qui part quand la
 * liste locale ne suffit plus. Les deux ne se marchent pas sur les pieds : la
 * frappe AFFINE ce qui est affiché, ce paramètre VA CHERCHER AILLEURS. C'est la
 * leçon du 10/09, où un déroulant « statut » filtrait au serveur pendant que la
 * recherche filtrait au navigateur, sans que personne sache lequel gagnait : ce
 * qu'il fallait éviter n'était pas deux endroits, mais deux endroits pour la
 * MÊME chose.
 */
/**
 * La liste, ET ce qu'elle ne montre pas : les ordres que la fenêtre de dates
 * écarte, et ceux que la coupe des 500 laisse derrière.
 *
 * `hors_periode` est arrivé le 04/09/2026 : l'écran disait « aucun ordre »
 * alors qu'il en cachait un, daté du mois précédent. Une fenêtre VIDE (`du` et
 * `au` à blanc) n'est pas envoyée du tout — c'est « toutes les dates », et
 * l'API ne filtre alors que sur le reste.
 *
 * `trouves` est arrivé le 26/10/2026, pour le même motif un étage plus haut :
 * la liste en rendait 500 sans dire qu'il y en avait 521. Il vaut la longueur
 * de la liste dans le cas ordinaire — le plafond ne se signale que quand il a
 * mordu, et c'est `trouves > ordres.length` qui le dit. L'API rend aussi son
 * `plafond` ; l'écran n'en a pas besoin, la longueur de la liste EST le plafond
 * dès qu'il a mordu.
 */
export async function lireOrdres(
  jeton: string,
  fenetre: {
    readonly du: string;
    readonly au: string;
    readonly statut?: string;
    readonly recherche?: string;
  },
): Promise<
  Resultat<{
    readonly ordres: readonly OrdreTransport[];
    readonly horsPeriode: number;
    readonly trouves: number;
  }>
> {
  const parametres = new URLSearchParams();
  if (fenetre.du !== '') parametres.set('du', fenetre.du);
  if (fenetre.au !== '') parametres.set('au', fenetre.au);
  if (fenetre.statut !== undefined && fenetre.statut !== '') {
    parametres.set('statut', fenetre.statut);
  }
  if (fenetre.recherche !== undefined && fenetre.recherche.trim() !== '') {
    parametres.set('q', fenetre.recherche.trim());
  }
  const echange = await appeler(`/ordres-transport?${parametres.toString()}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as {
    ordres_transport?: readonly OrdreTransport[];
    hors_periode?: number;
    trouves?: number;
  };
  const ordres = corps.ordres_transport ?? [];
  return {
    etat: 'ok',
    valeur: {
      ordres,
      horsPeriode: corps.hors_periode ?? 0,
      // UNE API PLUS ANCIENNE NE LE REND PAS : le repli dit « rien n'est
      // caché » plutôt que « tout est caché ». Un Worker en retard d'un
      // déploiement ne doit pas faire clignoter un avertissement faux.
      trouves: corps.trouves ?? ordres.length,
    },
  };
}

/** Une fiche complète : l'ordre ET ses étapes, toujours les deux. */
function fiche(corps: unknown): FicheOrdre {
  const lu = corps as Partial<FicheOrdre>;
  if (lu.ordre_transport === undefined) {
    throw new Error('corps sans « ordre_transport »');
  }
  return {
    ordre_transport: lu.ordre_transport,
    etapes: lu.etapes ?? [],
    marchandises: lu.marchandises ?? [],
  };
}

/**
 * LES LIGNES DU PACKAGING, REMPLACÉES EN UNE FOIS — F4, 01/11/2026.
 *
 * Route `PUT /ordres-transport/:id/marchandises` : la liste entière part, et
 * l'API la pose à la place de l'ancienne, fige le métrage et recalcule les
 * étapes. Rend la fiche à jour.
 */
export async function remplacerLesMarchandises(
  jeton: string,
  otId: string,
  marchandises: readonly Record<string, string | number | boolean | null>[],
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/marchandises`,
    jeton,
    { methode: 'PUT', corps: { marchandises } },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

export async function lireOrdre(
  jeton: string,
  id: string,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(`/ordres-transport/${encodeURIComponent(id)}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

/** Ce qu'un ordre ou une étape accepte comme champs écrits. */
export type ChampsOrdre = Readonly<Record<string, string | boolean | null | readonly string[]>>;

export async function creerOrdre(
  jeton: string,
  champs: ChampsOrdre,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler('/ordres-transport', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

export async function modifierOrdre(
  jeton: string,
  id: string,
  champs: ChampsOrdre,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(`/ordres-transport/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

/**
 * Annuler l'ordre. Sa propre route, et non un champ `statut` dans la
 * modification : les quatre autres statuts se déduisent des étapes, et un champ
 * aurait laissé croire qu'ils se posent aussi.
 */
export async function annulerOrdre(
  jeton: string,
  id: string,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(id)}/annulation`,
    jeton,
    { methode: 'POST', corps: {} },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

/**
 * Rouvrir un ordre annulé. C'est l'API qui décide du statut rendu — l'état réel
 * des étapes — et non l'écran : un ordre annulé dont tout était livré revient à
 * « Livré ».
 */
export async function rouvrirOrdre(
  jeton: string,
  id: string,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(id)}/reouverture`,
    jeton,
    { methode: 'POST', corps: {} },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

/**
 * Supprimer un ordre EMPORTE SES ÉTAPES — seule cascade du schéma, et elle est
 * là pour la saisie fautive. Un transport qui n'a pas eu lieu s'ANNULE et reste
 * dans l'histoire.
 *
 * Rend `vide` comme un succès : un `204` n'a pas de corps, et le confondre avec
 * une panne ferait disparaître de l'écran un ordre toujours en base.
 */
export async function supprimerOrdre(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/ordres-transport/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat === 'vide' || echange.etat === 'succes') {
    return { etat: 'ok', valeur: null };
  }
  return echec(echange);
}

/**
 * Les trois écritures d'étape rendent LA FICHE COMPLÈTE, et non l'étape seule.
 *
 * C'est le contrat de l'API, et il vaut mieux que l'inverse : les colonnes
 * calculées de l'ordre — première date, dernière date, villes, décompte des
 * litiges — changent justement quand une étape bouge. L'écran n'a rien à
 * recoller.
 */
export async function ajouterEtape(
  jeton: string,
  otId: string,
  champs: ChampsOrdre,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/etapes`,
    jeton,
    { methode: 'POST', corps: champs },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

export async function modifierEtape(
  jeton: string,
  otId: string,
  etapeId: string,
  champs: ChampsOrdre,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/etapes/${encodeURIComponent(etapeId)}`,
    jeton,
    { methode: 'PATCH', corps: champs },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

export async function supprimerEtape(
  jeton: string,
  otId: string,
  etapeId: string,
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/etapes/${encodeURIComponent(etapeId)}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

/**
 * Les motifs de litige de la société — les actifs comme les inactifs.
 *
 * Même raison que pour les motifs d'indisponibilité : l'écran de paramétrage a
 * besoin des deux, et un motif désactivé reste lisible sur les litiges passés.
 * Pas de suppression, ici non plus.
 */
export async function lireMotifsLitige(
  jeton: string,
): Promise<Resultat<readonly MotifLitige[]>> {
  const echange = await appeler('/motifs-litige', jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { motifs?: readonly MotifLitige[] };
  return { etat: 'ok', valeur: corps.motifs ?? [] };
}

export async function creerMotifLitige(
  jeton: string,
  nom: string,
): Promise<Resultat<MotifLitige>> {
  const echange = await appeler('/motifs-litige', jeton, {
    methode: 'POST',
    corps: { nom },
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { motif?: MotifLitige };
  if (corps.motif === undefined) {
    throw new Error('corps sans « motif »');
  }
  return { etat: 'ok', valeur: corps.motif };
}

export async function changerMotifLitige(
  jeton: string,
  id: string,
  champs: Readonly<{ nom?: string; actif?: boolean }>,
): Promise<Resultat<MotifLitige>> {
  const echange = await appeler(`/motifs-litige/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { motif?: MotifLitige };
  if (corps.motif === undefined) {
    throw new Error('corps sans « motif »');
  }
  return { etat: 'ok', valeur: corps.motif };
}

/**
 * Remettre les étapes d'un ordre dans l'ordre voulu.
 *
 * On envoie LA LISTE ENTIÈRE des identifiants, dans l'ordre voulu, et non le
 * rang d'une seule étape. Deux raisons, écrites au long dans
 * `apps/api/src/ordres_transport.ts` : l'index unique `(ot_id, rang)` est
 * vérifié à chaque ligne — un échange par deux `PATCH` échoue au premier — et
 * deux appels ne sont pas atomiques.
 */
export async function reordonnerEtapes(
  jeton: string,
  otId: string,
  etapes: readonly string[],
): Promise<Resultat<FicheOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/etapes`,
    jeton,
    { methode: 'PUT', corps: { etapes } },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: fiche(echange.corps) };
}

// ---------------------------------------------------------------------------
// Le planning — 04/09/2026
// ---------------------------------------------------------------------------

/**
 * La semaine entière en un seul appel.
 *
 * `reference` sert à UNE chose : dire quel chauffeur est attelé à quel camion
 * en tête de ligne. L'écran envoie aujourd'hui quand la semaine le contient,
 * et le premier jour sinon — sans quoi la ligne annoncerait l'attelage d'un
 * lundi qu'on ne regarde pas.
 */
export async function lirePlanning(
  jeton: string,
  fenetre: {
    readonly du: string;
    readonly au: string;
    readonly reference?: string;
  },
): Promise<Resultat<Planning>> {
  const parametres = new URLSearchParams({ du: fenetre.du, au: fenetre.au });
  if (fenetre.reference !== undefined) {
    parametres.set('reference', fenetre.reference);
  }
  const echange = await appeler(`/planning?${parametres.toString()}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }

  /**
   * LES QUATRE LISTES SONT REMISES D'APLOMB ICI, et ce n'est pas de la
   * précaution gratuite : le 04/09/2026, l'écran a été mis à jour avant l'API,
   * qui ne rendait pas encore `supports`. La page du planning est devenue
   * BLANCHE — « supports is not iterable » —, et un écran blanc ne dit rien à
   * personne.
   *
   * Entre deux mises en ligne, l'écran et l'API ne sont jamais tout à fait de
   * la même version. Une clé absente doit donc valoir « rien », et non faire
   * tomber la page : c'est la frontière qui s'en charge, une fois, plutôt que
   * chaque endroit qui lit ces listes.
   */
  const corps = echange.corps as Partial<Planning> | null;
  return {
    etat: 'ok',
    valeur: {
      planning: corps?.planning ?? {
        du: fenetre.du,
        au: fenetre.au,
        reference: fenetre.reference ?? fenetre.du,
      },
      vehicules: corps?.vehicules ?? [],
      etapes: corps?.etapes ?? [],
      a_affecter: corps?.a_affecter ?? [],
      supports: corps?.supports ?? [],
    },
  };
}

/**
 * Poser un camion, le retirer, ou reporter un transport — un seul appel.
 *
 * `vehicule_id: null` libère, et la clé est TOUJOURS envoyée : l'API refuse un
 * corps qui l'aurait perdue en chemin plutôt que de libérer sans qu'on l'ait
 * demandé.
 */
export async function affecterAuPlanning(
  jeton: string,
  demande: {
    readonly ot_id: string;
    readonly jour: string | null;
    readonly vehicule_id: string | null;
    readonly vers_le?: string | null;
  },
): Promise<Resultat<{ readonly etapes: number; readonly statut: string }>> {
  const echange = await appeler('/planning/affectation', jeton, {
    methode: 'POST',
    corps: {
      ot_id: demande.ot_id,
      jour: demande.jour,
      vehicule_id: demande.vehicule_id,
      ...(demande.vers_le === undefined || demande.vers_le === null
        ? {}
        : { vers_le: demande.vers_le }),
    },
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as {
    affectation?: { etapes: number; statut: string };
  };
  return {
    etat: 'ok',
    valeur: corps.affectation ?? { etapes: 0, statut: 'attente' },
  };
}

/**
 * L'ordre des lignes, envoyé ENTIER.
 *
 * Même raison que pour les étapes d'un ordre : deux appels ne sont pas
 * atomiques, et une flotte à moitié rangée serait pire que pas rangée du tout.
 */
export async function rangerLesVehicules(
  jeton: string,
  vehicules: readonly string[],
): Promise<Resultat<number>> {
  const echange = await appeler('/planning/ordre-des-vehicules', jeton, {
    methode: 'PUT',
    corps: { vehicules },
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: vehicules.length };
}

/**
 * RANGER LES BLOCS D'UNE CASE — 16/09/2026.
 *
 * `ordres` est la liste COMPLÈTE des ordres de la case, dans l'ordre voulu.
 * Une liste VIDE rend la case au tri par heure : c'est le geste inverse, et il
 * n'a pas besoin d'une route à lui.
 */
export async function rangerLaCase(
  jeton: string,
  vehiculeId: string,
  jour: string,
  ordres: readonly string[],
): Promise<Resultat<number>> {
  const echange = await appeler(
    `/planning/cases/${encodeURIComponent(vehiculeId)}/${encodeURIComponent(jour)}/ordre`,
    jeton,
    { methode: 'PUT', corps: { ordres } },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: ordres.length };
}

// ---------------------------------------------------------------------------
// Les listes réglées par la société — 04/09/2026
// ---------------------------------------------------------------------------

/**
 * Une ligne de liste réglée : un support, un type de véhicule.
 *
 * `emploi` est CALCULÉ par l'API — le nombre d'étapes ou de camions qui s'en
 * servent. L'écran l'affiche et s'en sert pour savoir ce qui peut encore se
 * supprimer ; il ne le tient jamais lui-même, sans quoi il compterait sur ce
 * qu'il a chargé plutôt que sur ce qui existe.
 */
export interface LigneReglee {
  readonly id: string;
  readonly nom: string;
  readonly abrege?: string | null;
  /**
   * Les DIMENSIONS, sur les supports seulement, en CENTIMÈTRES. Absentes des
   * autres listes réglées : `undefined` y signifie « cette liste n'en a pas »,
   * `null` sur un support signifie « pas encore mesuré ».
   */
  /**
   * CE QUI SE REND — sur les supports seulement, depuis le 06/09/2026. Une
   * palette Europe s'échange, un carton perdu non. `undefined` sur les listes
   * qui n'ont pas la notion.
   */
  readonly echangeable?: boolean;
  /**
   * CE QUI PART EN ANNEXE DE LA FACTURE — sur les types de document
   * seulement, depuis le 14/09/2026. `undefined` sur les listes qui n'ont pas
   * la notion.
   */
  readonly joindre_facture?: boolean;
  readonly longueur_cm?: number | null;
  readonly largeur_cm?: number | null;
  readonly hauteur_cm?: number | null;
  /** Saisi, jamais déduit. 1 = on ne gerbe pas. */
  readonly etages?: number;
  /**
   * L'unité et le tarif maison d'une nature de prestation annexe, depuis le
   * 15/09/2026. Les natures seules les portent — voir
   * `LISTE_NATURES_PRESTATION` dans `apps/api/src/parametres.ts`.
   */
  readonly unite?: string;
  readonly prix_defaut?: string | null;
  readonly actif: boolean;
  readonly emploi: number;
}

/** Ce qu'une écriture de liste réglée peut porter. */
export type ChampsListeReglee = Readonly<{
  nom?: string;
  abrege?: string | null;
  actif?: boolean;
  longueur_cm?: number | null;
  largeur_cm?: number | null;
  hauteur_cm?: number | null;
  etages?: number;
  /**
   * Ce support s'échange-t-il au quai ? Les supports seuls la portent — l'API
   * ne l'accepte que sur cette liste (`apps/api/src/parametres.ts`, ligne 252).
   */
  echangeable?: boolean;
  /**
   * Ce type de pièce part-il avec la facture chez Pennylane ? Les types de
   * document seuls la portent — l'API ne l'accepte que sur cette liste
   * (`apps/api/src/parametres.ts`, descripteur `LISTE_TYPES_DOCUMENT`).
   */
  joindre_facture?: boolean;
  /**
   * L'unité d'une nature de prestation annexe — « heure », « jour », « unite »,
   * « forfait », « kilometre ». Liste fermée côté API ET côté base : les deux
   * refusent une autre valeur.
   */
  unite?: string;
  /** Le tarif maison, PRÉ-REMPLI à la saisie. `null` l'efface. */
  prix_defaut?: string | null;
}>;

/** Les deux listes, et le seul endroit où leurs chemins sont écrits. */
export const LISTES_REGLEES = {
  supports: { chemin: 'supports', cleListe: 'supports', cleUne: 'support' },
  types: { chemin: 'types-vehicule', cleListe: 'types', cleUne: 'type' },
  types_document: {
    chemin: 'types-document',
    cleListe: 'types',
    cleUne: 'type',
  },
  // Les centres de profit, depuis le 07/09/2026. Même moule que les autres :
  // c'est le descripteur `LISTE_CENTRES` d'`apps/api/src/parametres.ts` qui
  // sert les routes, et rien n'est écrit deux fois.
  centres: { chemin: 'centres', cleListe: 'centres', cleUne: 'centre' },
  // Les natures de prestation annexe, depuis le 15/09/2026. Même moule :
  // descripteur `LISTE_NATURES_PRESTATION` côté API.
  natures_prestation: {
    chemin: 'natures-prestation',
    cleListe: 'natures',
    cleUne: 'nature',
  },
} as const;

export type NomListeReglee = keyof typeof LISTES_REGLEES;

export async function lireListeReglee(
  jeton: string,
  nom: NomListeReglee,
): Promise<Resultat<readonly LigneReglee[]>> {
  const liste = LISTES_REGLEES[nom];
  const echange = await appeler(`/${liste.chemin}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as Record<string, readonly LigneReglee[] | undefined>;
  return { etat: 'ok', valeur: corps[liste.cleListe] ?? [] };
}

export async function creerLigneReglee(
  jeton: string,
  nom: NomListeReglee,
  champs: ChampsListeReglee & Readonly<{ nom: string }>,
): Promise<Resultat<LigneReglee>> {
  const liste = LISTES_REGLEES[nom];
  const echange = await appeler(`/${liste.chemin}`, jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as Record<string, LigneReglee | undefined>;
  const ligne = corps[liste.cleUne];
  if (ligne === undefined) {
    throw new Error(`corps sans « ${liste.cleUne} »`);
  }
  return { etat: 'ok', valeur: ligne };
}

export async function modifierLigneReglee(
  jeton: string,
  nom: NomListeReglee,
  id: string,
  champs: ChampsListeReglee,
): Promise<Resultat<LigneReglee>> {
  const liste = LISTES_REGLEES[nom];
  const echange = await appeler(`/${liste.chemin}/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as Record<string, LigneReglee | undefined>;
  const ligne = corps[liste.cleUne];
  if (ligne === undefined) {
    throw new Error(`corps sans « ${liste.cleUne} »`);
  }
  return { etat: 'ok', valeur: ligne };
}

/** La réponse est vide : 204, sans corps. `etat: 'vide'` est donc un succès. */
export async function supprimerLigneReglee(
  jeton: string,
  nom: NomListeReglee,
  id: string,
): Promise<Resultat<null>> {
  const liste = LISTES_REGLEES[nom];
  const echange = await appeler(`/${liste.chemin}/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

/**
 * Supprimer un motif de litige.
 *
 * Ouverte le 04/09/2026 avec l'écran des Paramètres. La base refuse d'elle-même
 * dès qu'une étape porte le motif (`etapes.motif_litige_id` en `restrict`) : on
 * n'a ouvert que le cas qu'elle autorisait déjà, celui du motif créé par erreur
 * et jamais employé.
 */
export async function supprimerMotifLitige(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/motifs-litige/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// Les grilles tarifaires — 15/09/2026, lot 1 de l'Étape 20
//
// « J'aimerais que l'on ajoute le mécanisme d'indexation gasoil et donc de
// grille tarifaire. » L'indice de référence et la part gasoil sont des clauses
// DE GRILLE : sans grille, il n'y a rien sur quoi les poser.
// ---------------------------------------------------------------------------

/** Les 101 départements, dans l'ordre officiel — 2A et 2B entre le 29 et le 30. */
export interface Departement {
  readonly code: string;
  readonly nom: string;
}

export async function lireDepartements(
  jeton: string,
): Promise<Resultat<readonly Departement[]>> {
  const echange = await appeler('/departements', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { departements?: readonly Departement[] };
  return { etat: 'ok', valeur: corps.departements ?? [] };
}

/** Une grille tarifaire, telle que l'API la rend. */
export interface Grille {
  readonly id: string;
  readonly client_id: string;
  readonly client: string;
  readonly nom: string;
  /** « palette » | « kilometre » | « trajet ». Posée à la création, figée. */
  readonly unite: string;
  readonly style_origine: string;
  readonly style_destination: string;
  readonly origine_site_id: string | null;
  readonly origine_site: string | null;
  readonly origine_departement: string | null;
  readonly du: string;
  /** `null` = ouverte. Clôturer, c'est poser cette date. */
  readonly au: string | null;
  readonly indice_reference: string | null;
  readonly part_gazole: string | null;
  /** Compté par l'API : la liste l'affiche sans un appel par grille. */
  readonly lignes: number;
}

/** Une ligne de tarif : une destination et son prix. */
export interface LigneTarif {
  readonly id: string;
  readonly destination_site_id: string | null;
  readonly destination_site: string | null;
  readonly destination_departement: string | null;
  readonly destination_departement_nom: string | null;
  readonly sens: string;
  /** `null` = TOUS les matériels, et c'est le cas de presque toutes les lignes. */
  readonly vehicule_code: string | null;
  readonly vehicule: string | null;
  readonly prix: string;
  readonly poids_min_kg: number | null;
  readonly poids_max_kg: number | null;
  readonly km_min: number | null;
  readonly km_max: number | null;
  /**
   * LA TRANCHE DE NOMBRE DE PALETTES, 15/09/2026. Sur une grille à la palette,
   * `prix` est le TOTAL du transport pour qui tombe dans cette tranche — pas
   * un prix unitaire. « n'oublie pas que un prix a la palette n'est pas juste
   * un prix x le nb de palette mais un prix different pour chaque nombre de
   * pal ».
   */
  readonly palettes_min: number | null;
  readonly palettes_max: number | null;
}

export interface GrilleOuverte {
  readonly grille: Grille;
  readonly lignes: readonly LigneTarif[];
}

function corpsGrille(corps: unknown): GrilleOuverte {
  const lu = corps as { grille?: Grille; lignes?: readonly LigneTarif[] };
  if (lu.grille === undefined) throw new Error('corps sans « grille »');
  return { grille: lu.grille, lignes: lu.lignes ?? [] };
}

export async function lireLesGrilles(
  jeton: string,
  cloturees: boolean,
): Promise<Resultat<readonly Grille[]>> {
  const echange = await appeler(`/grilles${cloturees ? '?cloturees=1' : ''}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { grilles?: readonly Grille[] };
  return { etat: 'ok', valeur: corps.grilles ?? [] };
}

export async function lireLaGrille(
  jeton: string,
  id: string,
): Promise<Resultat<GrilleOuverte>> {
  const echange = await appeler(`/grilles/${encodeURIComponent(id)}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsGrille(echange.corps) };
}

/** L'unité et les deux styles sont OBLIGATOIRES et ne se modifieront plus. */
export async function creerUneGrille(
  jeton: string,
  champs: Readonly<Record<string, unknown>>,
): Promise<Resultat<GrilleOuverte>> {
  const echange = await appeler('/grilles', jeton, { methode: 'POST', corps: champs });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsGrille(echange.corps) };
}

/** Ni l'unité ni les styles : l'API les refuse, et c'est voulu. */
export async function modifierUneGrille(
  jeton: string,
  id: string,
  champs: Readonly<Record<string, unknown>>,
): Promise<Resultat<GrilleOuverte>> {
  const echange = await appeler(`/grilles/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsGrille(echange.corps) };
}

/**
 * DUPLIQUER — la seule façon de changer l'unité d'un accord. Rend en plus
 * `recopiees` et `sur` : une ligne que la nouvelle forme refuse ne fait pas
 * échouer la copie, mais l'écran doit pouvoir le dire.
 */
export async function dupliquerUneGrille(
  jeton: string,
  id: string,
  champs: Readonly<Record<string, unknown>>,
): Promise<Resultat<GrilleOuverte & { recopiees: number; sur: number }>> {
  const echange = await appeler(`/grilles/${encodeURIComponent(id)}/duplication`, jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  const lu = echange.corps as { recopiees?: number; sur?: number };
  return {
    etat: 'ok',
    valeur: {
      ...corpsGrille(echange.corps),
      recopiees: lu.recopiees ?? 0,
      sur: lu.sur ?? 0,
    },
  };
}

export async function supprimerUneGrille(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/grilles/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function ajouterUneLigneTarif(
  jeton: string,
  grilleId: string,
  champs: Readonly<Record<string, unknown>>,
): Promise<Resultat<GrilleOuverte>> {
  const echange = await appeler(
    `/grilles/${encodeURIComponent(grilleId)}/lignes`,
    jeton,
    { methode: 'POST', corps: champs },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsGrille(echange.corps) };
}

export async function retirerUneLigneTarif(
  jeton: string,
  grilleId: string,
  ligneId: string,
): Promise<Resultat<GrilleOuverte>> {
  const echange = await appeler(
    `/grilles/${encodeURIComponent(grilleId)}/lignes/${encodeURIComponent(ligneId)}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsGrille(echange.corps) };
}

// ---------------------------------------------------------------------------
// Les prestations annexes d'un ordre — 15/09/2026
//
// « Il faut pouvoir sur un OT ajouter les frais annexes pour ne pas les
// mélanger au transport initial même si il garde le même n° OT. »
// ---------------------------------------------------------------------------

/** Une prestation annexe, telle que l'API la rend. */
export interface PrestationAnnexe {
  readonly id: string;
  readonly nature_id: string;
  /** Le nom de la nature, LU d'elle — il suit donc un renommage. */
  readonly nature: string;
  /** « heure », « jour », « unite », « forfait », « kilometre ». */
  readonly unite: string;
  readonly detail: string | null;
  readonly quantite: string;
  readonly prix_unitaire: string;
  /** `quantite × prix_unitaire`, arrondi PAR LA BASE — jamais en JavaScript. */
  readonly total: string;
  /** Vrai dès qu'elle est descendue sur une facture : elle ne bouge plus. */
  readonly facturee: boolean;
}

/** Les prestations d'un ordre et leur somme, telles que les quatre routes les rendent. */
export interface PrestationsDeLOrdre {
  readonly prestations: readonly PrestationAnnexe[];
  /** La somme de TOUTES les prestations, facturées comprises. */
  readonly total: string;
}

function corpsPrestations(corps: unknown): PrestationsDeLOrdre {
  const lu = corps as {
    prestations?: readonly PrestationAnnexe[];
    total?: string;
  };
  return { prestations: lu.prestations ?? [], total: lu.total ?? '0' };
}

export async function lireLesPrestations(
  jeton: string,
  otId: string,
): Promise<Resultat<PrestationsDeLOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/prestations`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsPrestations(echange.corps) };
}

/**
 * Le PRIX EST FACULTATIF : sans lui, l'API prend celui de la nature. L'écran
 * n'a donc pas à recopier un chiffre qu'il vient d'afficher.
 */
export async function ajouterUnePrestation(
  jeton: string,
  otId: string,
  champs: Readonly<{
    nature_id: string;
    detail?: string | null;
    quantite?: string;
    prix_unitaire?: string;
  }>,
): Promise<Resultat<PrestationsDeLOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/prestations`,
    jeton,
    { methode: 'POST', corps: champs },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsPrestations(echange.corps) };
}

/**
 * LA NATURE NE FIGURE PAS ICI, et ce n'est pas un oubli : elle décide de
 * l'unité, donc du sens de la quantité. Se tromper de nature se corrige en
 * retirant la prestation et en la reposant.
 */
export async function modifierUnePrestation(
  jeton: string,
  otId: string,
  prestationId: string,
  champs: Readonly<{
    detail?: string | null;
    quantite?: string;
    prix_unitaire?: string;
  }>,
): Promise<Resultat<PrestationsDeLOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/prestations/${encodeURIComponent(
      prestationId,
    )}`,
    jeton,
    { methode: 'PATCH', corps: champs },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsPrestations(echange.corps) };
}

export async function retirerUnePrestation(
  jeton: string,
  otId: string,
  prestationId: string,
): Promise<Resultat<PrestationsDeLOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/prestations/${encodeURIComponent(
      prestationId,
    )}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: corpsPrestations(echange.corps) };
}

// ---------------------------------------------------------------------------
// Les documents d'un ordre — 04/09/2026
// ---------------------------------------------------------------------------

/** Une pièce jointe, telle que l'API la rend. Le FICHIER, lui, est ailleurs. */
export interface Document {
  readonly id: string;
  readonly ot_id: string;
  readonly type_id: string | null;
  readonly type: string | null;
  readonly nom: string;
  readonly chemin: string;
  /** `bigint` rendu en chaîne, comme les `numeric` : jamais un flottant. */
  readonly poids: string | null;
  readonly etat: string;
  readonly ajoute_par_nom: string | null;
  readonly cree_le: string;
}

export async function lireDocuments(
  jeton: string,
  otId: string,
): Promise<Resultat<readonly Document[]>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/documents`,
    jeton,
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { documents?: readonly Document[] };
  return { etat: 'ok', valeur: corps.documents ?? [] };
}

/**
 * DÉCLARER la pièce, avant d'envoyer le fichier.
 *
 * La réponse porte le chemin à employer : il est calculé par l'API et jamais
 * composé par l'écran — c'est ce qui garantit qu'il commence par la bonne
 * société.
 */
export async function declarerLeDocument(
  jeton: string,
  otId: string,
  champs: Readonly<{ nom: string; type_id?: string | null; poids?: number }>,
): Promise<Resultat<Document>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/documents`,
    jeton,
    { methode: 'POST', corps: champs },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { document?: Document };
  if (corps.document === undefined) {
    throw new Error('corps sans « document »');
  }
  return { etat: 'ok', valeur: corps.document };
}

/** Confirmer que le fichier est arrivé. Seule modification permise. */
export async function confirmerLeDocument(
  jeton: string,
  otId: string,
  documentId: string,
): Promise<Resultat<Document>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/documents/${encodeURIComponent(
      documentId,
    )}`,
    jeton,
    { methode: 'PATCH', corps: { etat: 'joint' } },
  );
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as { document?: Document };
  if (corps.document === undefined) {
    throw new Error('corps sans « document »');
  }
  return { etat: 'ok', valeur: corps.document };
}

export async function supprimerLeDocument(
  jeton: string,
  otId: string,
  documentId: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/documents/${encodeURIComponent(
      documentId,
    )}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// Les tournées — 05/09/2026
// ---------------------------------------------------------------------------

/**
 * Une tournée, telle que l'API la rend.
 *
 * Les caractéristiques du camion sont FIGÉES à l'attelage : immatriculations,
 * nom du chauffeur, capacité, charge utile. Ce ne sont pas des jointures — un
 * indicateur calculé sur l'historique ne doit pas bouger le jour où quelqu'un
 * corrige une fiche du parc.
 *
 * `capacite_ml`, `palettes` et `charge_utile` arrivent en CHAÎNES : ce sont des
 * `numeric`, et le pilote postgres les rend en texte.
 */
export interface LigneTournee {
  readonly id: string;
  readonly numero: string;
  readonly vehicule_id: string | null;
  readonly chauffeur_id: string | null;
  readonly remorque_id: string | null;
  readonly vehicule_immatriculation: string | null;
  readonly remorque_immatriculation: string | null;
  readonly chauffeur_nom: string | null;
  readonly capacite_ml: string | null;
  readonly palettes: number | null;
  readonly charge_utile: string | null;
  readonly attelee_le: string | null;
  readonly km_estimes: number | null;
  readonly km_reels: number | null;
  readonly note: string | null;
  /** CALCULÉE des arrêts, jamais saisie. `null` sur une tournée vide. */
  readonly jour_debut: string | null;
  readonly jour_fin: string | null;
  readonly arrets: number;
  readonly ordres: number;
}

/** Un arrêt ou une étape, même forme — c'est la même requête côté API. */
export interface LigneArret {
  readonly id: string;
  readonly tournee_id: string | null;
  readonly rang_tournee: number | null;
  readonly rang_chargement: number | null;
  readonly ot_id: string;
  readonly numero: string;
  readonly client_id: string | null;
  readonly client: string | null;
  readonly prix_vente: string | null;
  readonly distance_km: number | null;
  readonly rang: number;
  readonly type: string;
  readonly site_id: string | null;
  readonly site_nom: string;
  readonly site_code_postal: string | null;
  readonly site_ville: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  readonly quantite: string | null;
  readonly support: string | null;
  readonly ml_unitaire: string | null;
  readonly poids: number | null;
  readonly vehicule_id: string | null;
  readonly chauffeur_id: string | null;
  readonly remorque_id: string | null;
  readonly fait: boolean;
  readonly litige_ouvert: boolean;
}

/** Ce que la fenêtre de préparation rend, en UN SEUL appel. */
export interface Preparation {
  readonly tournees: readonly LigneTournee[];
  /** Les arrêts DES TOURNÉES, hors fenêtre compris. */
  readonly arrets: readonly LigneArret[];
  /** Les étapes de la fenêtre, placées ou non — le panneau de gauche. */
  readonly etapes: readonly LigneArret[];
}

/**
 * La fenêtre entière en un seul appel — tournées, arrêts, étapes.
 *
 * Deux appels donneraient deux instants différents, et l'écran montrerait une
 * étape déjà affectée comme étant encore à placer. Même raison que le planning,
 * qui lit sa semaine d'un coup.
 *
 * LES TROIS LISTES SONT REMISES D'APLOMB ICI, comme celles du planning : le
 * 04/09/2026, une clé absente a rendu la page du planning BLANCHE — « supports
 * is not iterable ». Entre deux mises en ligne, l'écran et l'API ne sont jamais
 * tout à fait de la même version.
 */
export async function lirePreparation(
  jeton: string,
  fenetre: { readonly du: string; readonly au: string },
): Promise<Resultat<Preparation>> {
  const parametres = new URLSearchParams({ du: fenetre.du, au: fenetre.au });
  const echange = await appeler(`/tournees?${parametres.toString()}`, jeton);
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  const corps = echange.corps as Partial<Preparation> | null;
  return {
    etat: 'ok',
    valeur: {
      tournees: corps?.tournees ?? [],
      arrets: corps?.arrets ?? [],
      etapes: corps?.etapes ?? [],
    },
  };
}

/** Ouvrir une tournée — VIDE ET SANS CAMION. On la monte, puis on l'attelle. */
export async function creerTournee(jeton: string): Promise<Resultat<LigneTournee>> {
  const echange = await appeler('/tournees', jeton, { methode: 'POST' });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return {
    etat: 'ok',
    valeur: (echange.corps as { tournee: LigneTournee }).tournee,
  };
}

/**
 * Atteler — ou dételer, en envoyant les trois à `null`.
 *
 * **Les trois clés sont TOUJOURS envoyées**, comme pour le planning : un corps
 * qui en aurait perdu une en chemin dételerait sans qu'on l'ait demandé.
 */
export async function attelerTournee(
  jeton: string,
  id: string,
  attelage: {
    readonly vehicule_id: string | null;
    readonly chauffeur_id: string | null;
    readonly remorque_id: string | null;
  },
): Promise<Resultat<LigneTournee>> {
  const echange = await appeler(`/tournees/${encodeURIComponent(id)}/attelage`, jeton, {
    methode: 'POST',
    corps: attelage,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return {
    etat: 'ok',
    valeur: (echange.corps as { tournee: LigneTournee }).tournee,
  };
}

/** Modifier la note ou les kilomètres. Rien d'autre ne se saisit sur une tournée. */
export async function modifierTournee(
  jeton: string,
  id: string,
  champs: {
    readonly note?: string | null;
    readonly km_estimes?: number | null;
    readonly km_reels?: number | null;
  },
): Promise<Resultat<LigneTournee>> {
  const echange = await appeler(`/tournees/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return {
    etat: 'ok',
    valeur: (echange.corps as { tournee: LigneTournee }).tournee,
  };
}

/**
 * Affecter des étapes — plusieurs d'un coup, dans l'ordre où l'écran les envoie.
 *
 * Une étape n'est jamais dans deux tournées : l'affecter ici la sort de la
 * précédente, sans qu'on ait à l'en retirer d'abord.
 */
export async function affecterALaTournee(
  jeton: string,
  id: string,
  etapes: readonly string[],
): Promise<Resultat<null>> {
  const echange = await appeler(`/tournees/${encodeURIComponent(id)}/etapes`, jeton, {
    methode: 'POST',
    corps: { etapes },
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

/**
 * Sortir une étape de sa tournée.
 *
 * **Elle GARDE son camion.** Sortir n'est pas déplanifier : la tournée est un
 * outil de préparation, pas le propriétaire de l'affectation.
 */
export async function sortirDeLaTournee(
  jeton: string,
  id: string,
  etapeId: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/tournees/${encodeURIComponent(id)}/etapes/${encodeURIComponent(etapeId)}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

/**
 * Réordonner la route — LA LISTE ENTIÈRE, dans l'ordre voulu.
 *
 * Jamais le rang d'un seul arrêt : l'index unique `(tournee_id, rang_tournee)`
 * est vérifié à chaque ligne, et un échange par deux appels échouerait au
 * premier. Même raison que les étapes d'un ordre.
 */
export async function reordonnerLaRoute(
  jeton: string,
  id: string,
  etapes: readonly string[],
): Promise<Resultat<null>> {
  const echange = await appeler(`/tournees/${encodeURIComponent(id)}/route`, jeton, {
    methode: 'POST',
    corps: { etapes },
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

/** Supprimer une tournée. La base refuse tant qu'un arrêt y est rattaché. */
export async function supprimerTournee(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/tournees/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

/**
 * L'ordre de chargement des lots — ce qu'on pose au fond, et dans quel ordre.
 *
 * Distinct de la route : la route dit dans quel ordre on PASSE, le chargement
 * dans quel ordre on POSE. La liste peut être PARTIELLE — un lot dont le
 * support n'a pas de dimensions n'est pas dessiné, donc pas déplaçable.
 */
export async function ordonnerLeChargement(
  jeton: string,
  id: string,
  etapes: readonly string[],
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/tournees/${encodeURIComponent(id)}/chargement`,
    jeton,
    { methode: 'POST', corps: { etapes } },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') {
    return echec(echange);
  }
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// L'emballage
// ---------------------------------------------------------------------------

/**
 * L'écran principal : les soldes, les bons vivants, les supports échangeables.
 *
 * TOUT EN UN SEUL APPEL. Deux appels donneraient deux instants différents, et
 * un bon marqué perdu entre les deux ferait afficher un solde qui ne
 * correspond à aucune liste.
 */
export async function lireEmballage(jeton: string): Promise<
  Resultat<{
    readonly soldes: readonly LigneSolde[];
    readonly bons: readonly Bon[];
    readonly supports: readonly Choix[];
  }>
> {
  const echange = await appeler('/emballage', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as {
    soldes?: readonly LigneSolde[];
    bons?: readonly Bon[];
    supports?: readonly Choix[];
  };
  return {
    etat: 'ok',
    valeur: {
      soldes: corps.soldes ?? [],
      bons: corps.bons ?? [],
      supports: corps.supports ?? [],
    },
  };
}

export interface FicheEmballage {
  readonly client: {
    readonly id: string;
    readonly nom: string;
    readonly code: string | null;
  };
  readonly soldes: readonly LigneSolde[];
  readonly mouvements: readonly MouvementEmballage[];
  readonly bons: readonly Bon[];
  readonly ouvertures: readonly {
    readonly site_id: string;
    readonly support: string;
    readonly quantite: number;
    readonly jour: string;
    readonly note: string | null;
  }[];
}

/** La fiche d'un client : les quatre onglets viennent du même appel. */
export async function lireFicheEmballage(
  jeton: string,
  clientId: string,
): Promise<Resultat<FicheEmballage>> {
  const echange = await appeler(
    `/emballage/clients/${encodeURIComponent(clientId)}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  const lu = echange.corps as Partial<FicheEmballage>;
  if (lu.client === undefined) throw new Error('corps sans « client »');
  return {
    etat: 'ok',
    valeur: {
      client: lu.client,
      soldes: lu.soldes ?? [],
      mouvements: lu.mouvements ?? [],
      bons: lu.bons ?? [],
      ouvertures: lu.ouvertures ?? [],
    },
  };
}

/**
 * Marquer un bon perdu — la seule écriture de jugement du module.
 *
 * L'API écrit AUSSI un mouvement de perte, dans la même transaction : sans
 * lui, l'abandon n'aurait aucun effet chiffré, le solde se calculant des
 * mouvements. L'écran recharge donc la fiche entière après ce geste.
 */
export async function marquerBonPerdu(
  jeton: string,
  id: string,
  note?: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/emballage/bons/${encodeURIComponent(id)}/perte`,
    jeton,
    { methode: 'POST', corps: note === undefined ? {} : { note } },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/**
 * L'échange d'une étape — appelé depuis la FICHE DE L'ORDRE, pas depuis
 * l'écran Emballage. Le cadrage §6 : « Échange choisi à la saisie de l'étape,
 * pas sur le site. »
 *
 * Un corps sans `support` efface l'échange : c'est ainsi qu'on corrige une
 * saisie faite sur la mauvaise étape.
 */
export async function poserLEchange(
  jeton: string,
  etapeId: string,
  echangeCorps: Readonly<Record<string, string | number>>,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/emballage/etapes/${encodeURIComponent(etapeId)}`,
    jeton,
    { methode: 'PUT', corps: echangeCorps },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// La recherche d'un sous-traitant
// ---------------------------------------------------------------------------

/** Un transporteur trouvé, avec de quoi l'APPELER. */
export interface TransporteurTrouve {
  readonly id: string;
  readonly nom: string;
  readonly code: string | null;
  readonly telephone: string | null;
  readonly courriel: string | null;
  readonly ville: string | null;
  readonly code_postal: string | null;
  readonly pays: string | null;
  /** Les codes matériel qu'il possède. `null` s'il n'en a aucun. */
  readonly vehicules: readonly string[] | null;
  readonly zones: number;
  /**
   * COMPTÉS, jamais stockés : le nombre d'affrètements non annulés avec ce
   * transporteur, et la date du dernier. Zéro et `null` au démarrage — c'est
   * pourquoi le classement a un troisième cran, le nom.
   */
  readonly transports: number;
  readonly dernier_affretement: string | null;
}

/**
 * Ce que chaque filtre écarte, quand la liste est vide.
 *
 * `null` dès qu'il y a un résultat : sur une recherche qui trouve, ces nombres
 * n'apprennent rien et l'API ne les calcule pas.
 */
export interface ExplicationVide {
  readonly sur_l_axe: number;
  readonly avec_le_materiel: number;
  readonly actifs: number;
}

export interface Recherche {
  readonly depart: string | null;
  readonly arrivee: string | null;
  readonly vehicule: string | null;
}

/**
 * Qui appeler pour cet axe, avec ce matériel.
 *
 * Les trois critères sont FACULTATIFS : relâcher un filtre est précisément ce
 * que l'écran propose quand il ne trouve personne.
 */
export async function chercherTransporteurs(
  jeton: string,
  critere: Recherche,
): Promise<
  Resultat<{
    readonly transporteurs: readonly TransporteurTrouve[];
    readonly explication: ExplicationVide | null;
  }>
> {
  const parametres = new URLSearchParams();
  if (critere.depart !== null && critere.depart !== '') {
    parametres.set('depart', critere.depart);
  }
  if (critere.arrivee !== null && critere.arrivee !== '') {
    parametres.set('arrivee', critere.arrivee);
  }
  if (critere.vehicule !== null && critere.vehicule !== '') {
    parametres.set('vehicule', critere.vehicule);
  }

  const echange = await appeler(
    `/affretement/transporteurs?${parametres.toString()}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as {
    transporteurs?: readonly TransporteurTrouve[];
    explication?: ExplicationVide | null;
  };
  return {
    etat: 'ok',
    valeur: {
      transporteurs: corps.transporteurs ?? [],
      explication: corps.explication ?? null,
    },
  };
}

/** Un affrètement, tel que l'API le rend. Le prix de vente vient de l'ordre. */
export interface Affretement {
  readonly id: string;
  readonly ot_id: string;
  readonly ot_numero: string;
  readonly transporteur_id: string;
  readonly transporteur_nom: string;
  readonly contact_nom: string | null;
  readonly contact_telephone: string | null;
  readonly prix_achat: string;
  readonly prix_vente: string | null;
  readonly vehicule_code: string | null;
  readonly vehicule: string | null;
  readonly zone_depart: string | null;
  readonly zone_arrivee: string | null;
  readonly instructions: string | null;
  readonly cree_le: string;
  readonly cree_par: string;
  readonly annule_le: string | null;
  readonly annule_par: string | null;
  readonly motif_annulation: string | null;
}

/**
 * Affréter un ordre.
 *
 * L'AXE N'EST PAS ENVOYÉ : l'API le déduit des étapes et le fige. Une saisie
 * de plus serait une saisie à tenir cohérente avec les étapes.
 */
export async function affreter(
  jeton: string,
  otId: string,
  corps: Readonly<Record<string, string>>,
): Promise<Resultat<Affretement>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/affretement`,
    jeton,
    { methode: 'POST', corps },
  );
  if (echange.etat !== 'succes') return echec(echange);
  const lu = echange.corps as { affretement?: Affretement };
  if (lu.affretement === undefined) throw new Error('corps sans « affretement »');
  return { etat: 'ok', valeur: lu.affretement };
}

/** Annuler un affrètement — la ligne reste, avec sa date et son motif. */
export async function annulerLAffretement(
  jeton: string,
  id: string,
  motif?: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/affretements/${encodeURIComponent(id)}/annulation`,
    jeton,
    { methode: 'POST', corps: motif === undefined ? {} : { motif } },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/** L'affrètement en cours d'un ordre, et ceux d'avant. */
export async function lireAffretements(
  jeton: string,
  otId: string,
): Promise<Resultat<readonly Affretement[]>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/affretements`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { affretements?: readonly Affretement[] };
  return { etat: 'ok', valeur: corps.affretements ?? [] };
}

// ---------------------------------------------------------------------------
// Les litiges — table à part depuis le 07/09/2026
// ---------------------------------------------------------------------------

/**
 * UNE LIGNE DE LA LISTE DES LITIGES.
 *
 * `jours_ouvert` est calculé EN BASE : c'est un nombre de jours, pas une date à
 * soustraire de tête. L'écran le colore, il ne le recalcule pas — deux calculs
 * du même nombre finiraient par diverger d'un jour au passage de minuit.
 *
 * `ot_statut` est le statut de l'ORDRE, pas celui du litige. Le cadrage insiste :
 * « un OT peut être livré et en litige ». Les deux ne se confondent pas.
 */
export interface Litige {
  readonly id: string;
  readonly etape_id: string;
  readonly motif_litige_id: string | null;
  readonly motif: string | null;
  readonly note: string | null;
  readonly ouvert_le: string;
  readonly ouvert_par: string;
  readonly resolu_le: string | null;
  readonly resolu_par: string | null;
  readonly resolution: string | null;
  readonly jours_ouvert: number;
  readonly jours_resolution: number | null;
  readonly etape_rang: number;
  readonly etape_type: string;
  readonly etape_site: string | null;
  readonly etape_ville: string | null;
  readonly etape_jour: string | null;
  readonly ot_id: string;
  readonly ot_numero: string;
  readonly ot_statut: string;
  readonly client: string;
  readonly prix_vente: string | null;
}

/**
 * CE QUE LA LISTE FAIT APPARAÎTRE, et qui n'est pas dans les lignes.
 *
 * Compté par l'API sur TOUS les litiges ouverts, jamais sur la page affichée :
 * un filtre par motif ne doit pas faire croire que l'argent bloqué a diminué.
 */
export interface DecompteLitiges {
  readonly ouverts: number;
  /** En jours. `null` quand il n'y a aucun litige ouvert. */
  readonly plus_ancien: number | null;
  /** Des ORDRES distincts, pas des litiges : deux litiges bloquent une facture. */
  readonly bloques: number;
  readonly montant: string;
}

export async function lireLitiges(
  jeton: string,
  filtres: {
    readonly etat?: 'ouverts' | 'resolus' | 'tous';
    readonly motif?: string;
    readonly q?: string;
  } = {},
): Promise<Resultat<{ litiges: readonly Litige[]; decompte: DecompteLitiges }>> {
  const parametres = new URLSearchParams();
  if (filtres.etat !== undefined) parametres.set('etat', filtres.etat);
  if (filtres.motif !== undefined && filtres.motif !== '') {
    parametres.set('motif', filtres.motif);
  }
  if (filtres.q !== undefined && filtres.q.trim() !== '') {
    parametres.set('q', filtres.q.trim());
  }
  const echange = await appeler(`/litiges?${parametres.toString()}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as {
    litiges?: readonly Litige[];
    decompte?: DecompteLitiges;
  };
  return {
    etat: 'ok',
    valeur: {
      litiges: corps.litiges ?? [],
      decompte: corps.decompte ?? {
        ouverts: 0,
        plus_ancien: null,
        bloques: 0,
        montant: '0',
      },
    },
  };
}

export async function ouvrirUnLitige(
  jeton: string,
  etapeId: string,
  champs: Readonly<{ motif_litige_id: string; note?: string; ouvert_le?: string }>,
): Promise<Resultat<Litige>> {
  const echange = await appeler(
    `/etapes/${encodeURIComponent(etapeId)}/litige`,
    jeton,
    { methode: 'POST', corps: champs },
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { litige?: Litige };
  if (corps.litige === undefined) throw new Error('corps sans « litige »');
  return { etat: 'ok', valeur: corps.litige };
}

export async function resoudreUnLitige(
  jeton: string,
  litigeId: string,
  champs: Readonly<{ resolution: string; resolu_le?: string }>,
): Promise<Resultat<Litige>> {
  const echange = await appeler(
    `/litiges/${encodeURIComponent(litigeId)}/resolution`,
    jeton,
    { methode: 'POST', corps: champs },
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { litige?: Litige };
  if (corps.litige === undefined) throw new Error('corps sans « litige »');
  return { etat: 'ok', valeur: corps.litige };
}

// ---------------------------------------------------------------------------
// Le module Finance — lot 1 : ce que chaque société paramètre
// ---------------------------------------------------------------------------

/** En propre, ou exploité pour le compte d'un autre. */
export type NatureRessource = 'propre' | 'tractionnaire';

export type SorteRessource = 'vehicule' | 'remorque' | 'chauffeur';

/**
 * UNE RESSOURCE ET SON COÛT, pour le mois demandé.
 *
 * `jours_exploites` et le prix viennent de l'API : le premier est compté sur un
 * VRAI calendrier — un mois a cinq samedis, un autre quatre — et le second est
 * celui qui était en vigueur ce mois-là, pas celui d'aujourd'hui.
 *
 * Tout ce qui touche au coût est `null` tant qu'aucun coût n'a été saisi. C'est
 * la règle du dépôt : montrer ce qui manque plutôt que calculer avec un trou.
 */
export interface RessourceFinance {
  readonly sorte: SorteRessource;
  readonly id: string;
  readonly nom: string;
  /** Le type du camion, le gabarit de la remorque. `null` pour un chauffeur. */
  readonly detail: string | null;
  readonly actif: boolean;
  readonly nature: NatureRessource;
  readonly cout_id: string | null;
  readonly prix_mensuel: string | null;
  readonly lundi: boolean | null;
  readonly mardi: boolean | null;
  readonly mercredi: boolean | null;
  readonly jeudi: boolean | null;
  readonly vendredi: boolean | null;
  readonly samedi: boolean | null;
  readonly dimanche: boolean | null;
  readonly consommation: string | null;
  readonly cout_du: string | null;
  readonly jours_exploites: number | null;
  readonly frais_ouverts: number;
}

export interface ReglagesFinance {
  /** En heures. `null` = PAS ENCORE RÉGLÉE, et l'écran le dit. */
  readonly amplitude_heures: string | null;
  readonly rattachement_ca: string;
}

export type UniteFrais = 'mois' | 'km' | 'heure';

export interface FraisFinance {
  readonly id: string;
  readonly libelle: string;
  readonly montant: string;
  readonly unite: UniteFrais;
  readonly ressource_sorte: SorteRessource | null;
  readonly ressource_nom: string | null;
  readonly vehicule_id: string | null;
  readonly remorque_id: string | null;
  readonly chauffeur_id: string | null;
  readonly du: string;
  readonly au: string | null;
}

export interface ContratTraction {
  readonly id: string;
  readonly transporteur_id: string;
  readonly transporteur: string;
  readonly vehicule_id: string | null;
  readonly vehicule: string | null;
  readonly chauffeur_id: string | null;
  readonly chauffeur: string | null;
  readonly mode: 'jour' | 'km' | 'voyage';
  readonly prix: string;
  readonly du: string;
  readonly au: string | null;
}

export interface IndiceGazole {
  readonly mois: string;
  /**
   * FACULTATIF ET PLUS LU DEPUIS LE LOT 136 (02/11/2026) : le prix qui sert au
   * coût est `prix_calcule`. Reste rendu pour les mois publiés avant.
   */
  readonly prix_litre: string | null;
  readonly indice: string | null;
  readonly publie_le: string;
  /**
   * LE PRIX DU LITRE QUI SERT AU COÛT — lot 137 : indice de base + indexation,
   * c'est-à-dire le taux du mois. `null` sans taux.
   */
  readonly prix_calcule: string | null;
  /**
   * L'indice de base de la société en vigueur ce mois-là, et l'indexation
   * (taux − base). `null` sans référence saisie, ou sans taux. Rendus par le
   * lot 137 ; facultatifs pour qu'un jeu d'essai ancien reste valable.
   */
  readonly indice_reference?: string | null;
  readonly indexation?: string | null;
}

/** L'indice de référence de la société, daté — lots 136 et 137, 02/11/2026. */
export interface ReferenceCarburantLue {
  readonly id: string;
  readonly indice: string;
  readonly du: string;
}

export async function lireRessourcesFinance(
  jeton: string,
  mois: string,
): Promise<
  Resultat<{
    mois: string;
    ressources: readonly RessourceFinance[];
    reglages: ReglagesFinance;
  }>
> {
  const parametres = new URLSearchParams();
  if (mois !== '') parametres.set('mois', mois);
  const echange = await appeler(`/finance/ressources?${parametres.toString()}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as {
    mois?: string;
    ressources?: readonly RessourceFinance[];
    reglages?: ReglagesFinance;
  };
  return {
    etat: 'ok',
    valeur: {
      mois: corps.mois ?? mois,
      ressources: corps.ressources ?? [],
      reglages: corps.reglages ?? {
        amplitude_heures: null,
        rattachement_ca: 'chargement',
      },
    },
  };
}

export async function reglerLaFinance(
  jeton: string,
  champs: Readonly<{ amplitude_heures?: string | null; rattachement_ca?: string }>,
): Promise<Resultat<ReglagesFinance>> {
  const echange = await appeler('/finance/reglages', jeton, {
    methode: 'PUT',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { reglages: ReglagesFinance }).reglages,
  };
}

export async function changerLaNature(
  jeton: string,
  sorte: SorteRessource,
  id: string,
  nature: NatureRessource,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/finance/ressources/${sorte}/${encodeURIComponent(id)}/nature`,
    jeton,
    { methode: 'PUT', corps: { nature } },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/**
 * OUVRIR UN COÛT. L'API ferme le précédent la veille — on n'envoie pas de date
 * de fin, et c'est le point : un prix ne se remplace pas.
 */
export async function poserUnCout(
  jeton: string,
  champs: Readonly<Record<string, string | boolean>>,
): Promise<Resultat<{ id: string }>> {
  const echange = await appeler('/finance/couts', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: (echange.corps as { cout: { id: string } }).cout };
}

export async function supprimerUnCout(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/finance/couts/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// LES COÛTS PAR CENTRE DE PROFIT — 30/10/2026
// ---------------------------------------------------------------------------

/** Un centre avec son coût COURANT, tel que `GET /finance/centres` le rend. */
export interface CentreDeProfitLu {
  readonly id: string;
  readonly nom: string;
  /** `null` = aucun coût saisi. La différence avec « 0 » compte. */
  readonly cout_id: string | null;
  readonly cout_journalier: string | null;
  readonly consommation: string | null;
  readonly frais_km: string | null;
  readonly du: string | null;
  readonly lundi: boolean | null;
  readonly mardi: boolean | null;
  readonly mercredi: boolean | null;
  readonly jeudi: boolean | null;
  readonly vendredi: boolean | null;
  readonly samedi: boolean | null;
  readonly dimanche: boolean | null;
}

/** Un ensemble et le centre auquel il est rattaché, avec depuis quand. */
export interface EnsembleDuCentre {
  readonly id: string;
  readonly immatriculation: string;
  readonly centre_id: string | null;
  /**
   * Depuis quand ce rattachement vaut. `null` quand il n'y en a aucun.
   *
   * LE RATTACHEMENT EST DATÉ depuis la migration `20261030180000` : changer un
   * camion de service ne doit pas rechiffrer ses jours creux des mois passés.
   */
  readonly centre_depuis: string | null;
}

/**
 * LES CENTRES ET LES ENSEMBLES EN UNE SEULE LECTURE.
 *
 * Les deux ensemble, parce que l'un sans l'autre ne sert à rien : un coût posé
 * sur un centre qu'aucun camion ne porte ne chiffre aucun jour creux.
 */
export async function lireLesCentresDeProfit(jeton: string): Promise<
  Resultat<{
    readonly centres: readonly CentreDeProfitLu[];
    readonly vehicules: readonly EnsembleDuCentre[];
  }>
> {
  const echange = await appeler('/finance/centres', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: echange.corps as {
      centres: readonly CentreDeProfitLu[];
      vehicules: readonly EnsembleDuCentre[];
    },
  };
}

/**
 * POSER LE COÛT D'UN CENTRE. L'API ferme le précédent la veille — on n'envoie
 * pas de date de fin, et c'est le point : un coût corrigé en octobre ne doit
 * pas réécrire la marge de juillet.
 */
export async function poserUnCoutDeCentre(
  jeton: string,
  champs: Readonly<Record<string, string | boolean>>,
): Promise<Resultat<{ id: string }>> {
  const echange = await appeler('/finance/centres/couts', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: (echange.corps as { cout: { id: string } }).cout };
}

export async function supprimerUnCoutDeCentre(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/finance/centres/couts/${encodeURIComponent(id)}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/**
 * RATTACHER UN ENSEMBLE À UN CENTRE — ou le détacher avec `null`.
 *
 * Sert aux JOURS CREUX, et à rien d'autre : un camion qui ne roule pas n'a
 * aucun transport, donc aucun centre à déduire. Les journées roulées prennent
 * le centre de leurs ordres.
 */
export async function rattacherUnEnsembleAUnCentre(
  jeton: string,
  vehiculeId: string,
  centreId: string | null,
  du?: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/finance/vehicules/${encodeURIComponent(vehiculeId)}/centre`,
    jeton,
    {
      methode: 'PUT',
      // `du` OMIS = aujourd'hui. L'API ferme la période précédente la veille :
      // on n'envoie jamais de date de fin, c'est elle qui la pose.
      corps: du === undefined ? { centre_id: centreId } : { centre_id: centreId, du },
    },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function lireFrais(
  jeton: string,
): Promise<Resultat<readonly FraisFinance[]>> {
  const echange = await appeler('/finance/frais', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { frais?: readonly FraisFinance[] }).frais ?? [],
  };
}

export async function creerUnFrais(
  jeton: string,
  champs: Readonly<Record<string, string>>,
): Promise<Resultat<FraisFinance>> {
  const echange = await appeler('/finance/frais', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: (echange.corps as { frais: FraisFinance }).frais };
}

export async function fermerUnFrais(
  jeton: string,
  id: string,
  au: string,
): Promise<Resultat<FraisFinance>> {
  const echange = await appeler(`/finance/frais/${encodeURIComponent(id)}/fin`, jeton, {
    methode: 'POST',
    corps: { au },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: (echange.corps as { frais: FraisFinance }).frais };
}

export async function supprimerUnFrais(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/finance/frais/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function lireContrats(
  jeton: string,
): Promise<Resultat<readonly ContratTraction[]>> {
  const echange = await appeler('/finance/contrats', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { contrats?: readonly ContratTraction[] }).contrats ?? [],
  };
}

export async function creerUnContrat(
  jeton: string,
  champs: Readonly<Record<string, string>>,
): Promise<Resultat<ContratTraction>> {
  const echange = await appeler('/finance/contrats', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { contrat: ContratTraction }).contrat,
  };
}

export async function supprimerUnContrat(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/finance/contrats/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function lireGazole(
  jeton: string,
): Promise<Resultat<readonly IndiceGazole[]>> {
  const echange = await appeler('/finance/gazole', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { indices?: readonly IndiceGazole[] }).indices ?? [],
  };
}

/**
 * PUBLIER LE GAZOLE D'UN MOIS — 15/09/2026, Paramètres › Indexation carburant.
 *
 * `mois` s'écrit « AAAA-MM », exactement comme `lireGazole` le rend : l'écran
 * renvoie ce qu'il a lu, sans reformater.
 *
 * REPUBLIER LE MÊME MOIS LE CORRIGE — l'API fait un `on conflict do update`.
 * Il n'y a donc pas de route de modification à part, et l'écran n'a pas à
 * savoir si le mois existe déjà avant d'envoyer.
 */
/**
 * L'ÉCRAN INDEXATION CARBURANT EN UNE LECTURE — les mois publiés et les
 * indices de référence de la société. Lots 136 et 137, 02/11/2026.
 */
export async function lireIndexationCarburant(jeton: string): Promise<
  Resultat<{
    indices: readonly IndiceGazole[];
    references: readonly ReferenceCarburantLue[];
  }>
> {
  const echange = await appeler('/finance/gazole', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as {
    indices?: readonly IndiceGazole[];
    references?: readonly ReferenceCarburantLue[];
  };
  return {
    etat: 'ok',
    valeur: { indices: corps.indices ?? [], references: corps.references ?? [] },
  };
}

/** Poser (ou corriger, pour la même date) l'indice de référence de la société. */
export async function poserReferenceCarburant(
  jeton: string,
  champs: Readonly<{ indice: string; du: string }>,
): Promise<Resultat<ReferenceCarburantLue>> {
  const echange = await appeler('/finance/references-carburant', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { reference: ReferenceCarburantLue }).reference,
  };
}

/** Effacer un indice de référence saisi de travers. */
export async function effacerReferenceCarburant(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/finance/references-carburant/${encodeURIComponent(id)}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

export async function publierLeGazole(
  jeton: string,
  champs: Readonly<{ mois: string; indice: string }>,
): Promise<Resultat<IndiceGazole>> {
  const echange = await appeler('/finance/gazole', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { indice: IndiceGazole }).indice,
  };
}

// ---------------------------------------------------------------------------
// Le coût de revient, lot 2 : ce qu'un transport a coûté
// ---------------------------------------------------------------------------

/**
 * LE COÛT D'UN ORDRE — le figé, et celui du moment.
 *
 * `fige` est ce qui a été arrêté au calcul : c'est LUI qui fait foi, et il ne
 * bouge pas quand un prix change ensuite. `calcule` est ce que le calcul
 * donnerait maintenant, avec la liste de CE QUI MANQUE — c'est elle qui permet
 * à l'écran de dire quoi saisir au lieu d'afficher un tiret.
 */
export interface CoutOrdre {
  readonly fige: {
    readonly cout_temps: string;
    readonly cout_km: string;
    readonly cout_peage: string;
    /**
     * LE PRIX PAYÉ AU SOUS-TRAITANT. `null` sur un transport fait en propre —
     * pas d'achat du tout, ce qui n'est pas zéro euro d'achat.
     */
    readonly cout_achat: string | null;
    readonly cout_fige_le: string;
  } | null;
  readonly calcule: {
    readonly cout_temps: number;
    readonly cout_km: number;
    readonly cout_peage: number;
    readonly cout_achat: number | null;
    readonly manques: readonly string[];
  };
  readonly prix_vente: string | null;
  readonly temps_reel_minutes: number | null;
  readonly peage: string | null;
  /**
   * L'INDEXATION GASOIL — 15/09/2026, Étape 20 lot 4.
   *
   * ELLE VOYAGE AVEC LE COÛT et non sur une route à elle : cet écran est déjà
   * celui qui montre `prix_vente`, et le prix indexé est le vrai produit de
   * l'ordre. Deux appels pour dessiner une seule ligne laisseraient la marge se
   * calculer sans elle le jour où l'un des deux échoue.
   *
   * `montant` est `null` dès qu'il manque quelque chose, et `manques` dit
   * quoi — jamais un zéro, qui se lirait « la clause n'a rien donné ».
   */
  readonly indexation: {
    readonly montant: string | null;
    readonly base: string | null;
    /** Le prix de vente plus la surcharge, additionnés par PostgreSQL. */
    readonly prix_indexe: string | null;
    readonly mois: string | null;
    readonly valeur_du_mois: string | null;
    readonly reference: string | null;
    readonly part: string | null;
    readonly grille: string | null;
    readonly manques: readonly string[];
    /** F3, 01/11/2026 : `null` la grille décide, `false` retirée, `true` demandée. */
    readonly forcee?: boolean | null;
    /** F3 : une grille avec clause gasoil s'applique — ce qui déduit « Avec ». */
    readonly clause?: boolean;
  };
}

/** L'indexation telle que l'API la rend, seule. */
export type IndexationLue = CoutOrdre['indexation'];

/**
 * L'INDEXATION D'UNE SAISIE EN COURS — F3, 01/11/2026.
 *
 * Route `POST /indexation-apercu` : rien n'est écrit, le calcul est celui des
 * ordres enregistrés. Sert au bloc Prix d'un ordre neuf, avant d'enregistrer.
 */
export async function apercuIndexation(
  jeton: string,
  corps: {
    readonly client_id: string;
    readonly prix_vente: string | null;
    readonly jour: string | null;
    readonly depart_site_id: string | null;
    readonly depart_code_postal: string | null;
    readonly indexation: boolean | null;
  },
): Promise<Resultat<IndexationLue>> {
  const echange = await appeler('/indexation-apercu', jeton, {
    methode: 'POST',
    corps,
  });
  if (echange.etat !== 'succes') {
    return echec(echange);
  }
  return {
    etat: 'ok',
    valeur: (echange.corps as { indexation: IndexationLue }).indexation,
  };
}

export async function lireLeCout(
  jeton: string,
  otId: string,
): Promise<Resultat<CoutOrdre>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/cout`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as CoutOrdre };
}

/**
 * LE TEMPS RÉEL ET LE PÉAGE. Le coût se fige dans la foulée, si tout est là —
 * c'est le moment où l'on sait enfin combien de temps le transport a pris.
 */
export async function poserLExecution(
  jeton: string,
  otId: string,
  champs: Readonly<{ temps_reel_minutes?: number; peage?: string | null }>,
): Promise<Resultat<{ manques: readonly string[] }>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/execution`,
    jeton,
    { methode: 'PUT', corps: champs },
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { cout?: { manques?: readonly string[] } };
  return { etat: 'ok', valeur: { manques: corps.cout?.manques ?? [] } };
}

export async function recalculerLeCout(
  jeton: string,
  otId: string,
): Promise<Resultat<{ manques: readonly string[] }>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/cout`,
    jeton,
    { methode: 'POST' },
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { cout?: { manques?: readonly string[] } };
  return { etat: 'ok', valeur: { manques: corps.cout?.manques ?? [] } };
}

export async function poserLePeageDeTournee(
  jeton: string,
  id: string,
  peage: string | null,
): Promise<Resultat<null>> {
  const echange = await appeler(`/tournees/${encodeURIComponent(id)}/peage`, jeton, {
    methode: 'PUT',
    corps: { peage },
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// ---------------------------------------------------------------------------
// L'import par fichier CSV — la reprise des données
// ---------------------------------------------------------------------------
// Ouvert le 07/09/2026. Trois appels : le modèle, l'analyse, l'import.

/** Les six sortes de fichier, telles que l'API les nomme dans son chemin. */
export type SorteImport =
  'clients' | 'sites' | 'transporteurs' | 'vehicules' | 'remorques' | 'chauffeurs';

export interface FauteImport {
  /** Le numéro de la ligne DANS LE FICHIER — celui du tableur. */
  readonly numero: number;
  readonly message: string;
}

export interface RapportImport {
  /** Ce qui empêche de lire le fichier entier. Vide = lisible. */
  readonly refus: readonly string[];
  readonly total: number;
  readonly fautes: readonly FauteImport[];
  readonly apercu: readonly string[];
}

const RAPPORT_VIDE: RapportImport = { refus: [], total: 0, fautes: [], apercu: [] };

function rapportDe(corps: unknown): RapportImport {
  const lu = (corps as { rapport?: Partial<RapportImport> } | undefined)?.rapport;
  if (lu === undefined) return RAPPORT_VIDE;
  return {
    refus: lu.refus ?? [],
    total: lu.total ?? 0,
    fautes: lu.fautes ?? [],
    apercu: lu.apercu ?? [],
  };
}

/**
 * LE MODÈLE À REMPLIR, en OCTETS et non en texte.
 *
 * `Response.text()` RETIRE la marque d'ordre d'octets en décodant — c'est le
 * standard fetch, constaté le 07/09/2026 en écrivant le test de la route.
 * Passer par `blob()` est donc obligatoire : sans la marque, Excel ouvrirait le
 * modèle en Windows-1252 et réenregistrerait tous les accents cassés.
 */
export async function telechargerModele(
  jeton: string,
  sorte: SorteImport,
): Promise<Resultat<Blob>> {
  let reponse: Response;
  try {
    reponse = await fetch(`${configuration.apiUrl}/import/${sorte}/modele`, {
      headers: { Authorization: `Bearer ${jeton}` },
    });
  } catch {
    return { etat: 'echec', cause: 'injoignable' };
  }
  if (!reponse.ok) {
    return {
      etat: 'echec',
      cause: reponse.status === 401 || reponse.status === 403 ? 'acces' : 'injoignable',
    };
  }
  return { etat: 'ok', valeur: await reponse.blob() };
}

/** ANALYSER : dit ce qui passerait. N'écrit rien. */
export async function analyserImport(
  jeton: string,
  sorte: SorteImport,
  csv: string,
): Promise<Resultat<RapportImport>> {
  const echange = await appeler(`/import/${sorte}/analyse`, jeton, {
    methode: 'POST',
    corps: { csv },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: rapportDe(echange.corps) };
}

/**
 * IMPORTER : écrit, tout ou rien.
 *
 * Trois issues et non deux. Un fichier fautif n'est pas une panne — c'est une
 * réponse qui PORTE LE RAPPORT, et l'écran doit pouvoir l'afficher plutôt que
 * de dire « données invalides ». C'est pourquoi cet appel ne rend pas un
 * `Resultat` ordinaire.
 */
export type ResultatImport =
  | { readonly etat: 'ok'; readonly importees: number }
  | { readonly etat: 'refuse'; readonly rapport: RapportImport }
  | { readonly etat: 'echec'; readonly cause: EchecApi };

export async function lancerImport(
  jeton: string,
  sorte: SorteImport,
  csv: string,
): Promise<ResultatImport> {
  const echange = await appeler(`/import/${sorte}`, jeton, {
    methode: 'POST',
    corps: { csv },
  });
  if (echange.etat === 'succes') {
    const corps = echange.corps as { importees?: number };
    return { etat: 'ok', importees: corps.importees ?? 0 };
  }
  if (
    echange.etat === 'refus' &&
    (echange.code === 'import_refuse' || echange.code === 'import_vide')
  ) {
    return { etat: 'refuse', rapport: rapportDe(echange.corps) };
  }
  return echec(echange);
}

// ---------------------------------------------------------------------------
// L'écran d'accueil
// ---------------------------------------------------------------------------

/**
 * TOUT L'ACCUEIL EN UN SEUL APPEL, et c'est la raison d'être de la route
 * `/accueil` côté API : six blocs feraient six allers-retours et surtout six
 * instants différents — le compteur d'une tuile pourrait contredire la liste
 * juste en dessous.
 *
 * Chaque clé est remise d'aplomb ici, comme pour le planning : entre deux mises
 * en ligne, l'écran et l'API ne sont jamais tout à fait de la même version, et
 * une clé absente doit valoir « rien » plutôt que faire une page blanche.
 */
export interface EtapeDuJour {
  readonly id: string;
  readonly ot_id: string;
  readonly numero: string;
  readonly client: string;
  readonly type: string;
  readonly site: string;
  readonly ville: string | null;
  readonly heure: string | null;
}

export interface OrdreAAffecter {
  readonly id: string;
  readonly numero: string;
  readonly client: string;
  readonly jour: string;
  readonly arrets: number;
}

export interface LitigeAccueil {
  readonly id: string;
  readonly ot_id: string;
  readonly numero: string;
  readonly client: string;
  readonly motif: string;
  readonly site: string;
  readonly jours: number;
}

export interface BonAStatuer {
  readonly id: string;
  readonly numero: string;
  readonly client: string;
  readonly support: string;
  readonly quantite: number;
  readonly expire_le: string;
  readonly jours: number;
}

export interface Accueil {
  readonly mois: {
    /** `numeric` rendu en chaîne : jamais converti en flottant. */
    readonly ca: string;
    readonly marge: string;
    readonly ordres: number;
    readonly ordres_sans_cout: number;
    readonly kilometres: number;
    readonly affretes: number;
  };
  readonly journee: {
    readonly total: number;
    readonly faites: number;
    readonly a_faire: readonly EtapeDuJour[];
  };
  readonly a_affecter: {
    readonly combien: number;
    readonly ordres: readonly OrdreAAffecter[];
  };
  readonly litiges: {
    readonly combien: number;
    readonly lignes: readonly LitigeAccueil[];
  };
  readonly bons: {
    readonly combien: number;
    readonly palettes: number;
    readonly lignes: readonly BonAStatuer[];
  };
  readonly non_classes: number;
}

const ACCUEIL_VIDE: Accueil = {
  mois: {
    ca: '0',
    marge: '0',
    ordres: 0,
    ordres_sans_cout: 0,
    kilometres: 0,
    affretes: 0,
  },
  journee: { total: 0, faites: 0, a_faire: [] },
  a_affecter: { combien: 0, ordres: [] },
  litiges: { combien: 0, lignes: [] },
  bons: { combien: 0, palettes: 0, lignes: [] },
  non_classes: 0,
};

export async function lireAccueil(jeton: string): Promise<Resultat<Accueil>> {
  const echange = await appeler('/accueil', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as Partial<Accueil> | null;
  return {
    etat: 'ok',
    valeur: {
      mois: corps?.mois ?? ACCUEIL_VIDE.mois,
      journee: corps?.journee ?? ACCUEIL_VIDE.journee,
      a_affecter: corps?.a_affecter ?? ACCUEIL_VIDE.a_affecter,
      litiges: corps?.litiges ?? ACCUEIL_VIDE.litiges,
      bons: corps?.bons ?? ACCUEIL_VIDE.bons,
      non_classes: corps?.non_classes ?? 0,
    },
  };
}

// ===========================================================================
// Le module Comptabilité — la facturation
// ===========================================================================
// Cadrage « Stafa v2 — Étape 10 : la facturation et le recouvrement »,
// clos le 09/09/2026.
//
// AUCUNE DE CES FONCTIONS NE CALCULE. Les totaux, la TVA et les états se
// déduisent dans `facturation.ts` ; ici on ne fait que porter des octets.

/** Une facture ou un avoir, tels que l'API les rend. */
export interface Facture {
  readonly id: string;
  readonly type: string;
  readonly numero: string | null;
  readonly client_id: string;
  readonly client_nom: string;
  readonly client_adresse: string | null;
  readonly client_code_postal: string | null;
  readonly client_ville: string | null;
  readonly client_pays: string | null;
  readonly client_tva: string | null;
  readonly reference_client: string | null;
  readonly date_piece: string;
  readonly echeance: string | null;
  readonly remise_globale: string | null;
  readonly facture_origine_id: string | null;
  readonly facture_origine_numero: string | null;
  readonly emise_le: string | null;
  readonly emise_par: string | null;
  /**
   * NON NUL QUAND LA PIÈCE A ÉTÉ DÉCLARÉE DÉJÀ FACTURÉE AILLEURS — 16/09/2026.
   *
   * Elle n'est pas passée par la validation, donc pas par les mêmes contrôles.
   * L'écran en fait une pastille : le dire vaut mieux que de la faire
   * ressembler aux autres.
   *
   * CE N'EST PAS UN SECOND `emise_le`. Celui-là porte la date de la PIÈCE,
   * celle que le client lit ; celle-ci porte la date du GESTE dans Stafa.
   */
  readonly emission_declaree_le: string | null;
  readonly envoye_le: string | null;
  readonly envoye_canal: string | null;
  readonly note: string | null;
}

/** Une ligne de facture. */
export interface LigneFacture {
  readonly id: string;
  readonly facture_id: string;
  readonly rang: number;
  readonly designation: string;
  /**
   * CE QUI S'IMPRIME SOUS LE LIBELLÉ — la date, les références, l'enlèvement,
   * la livraison et la charge, déjà composés avec leurs retours à la ligne.
   * Construit à la création et FIGÉ, corrigeable à la main sur un brouillon.
   * `null` sur une surcharge, et sur toute ligne d'avant le 14/09/2026.
   */
  readonly detail: string | null;
  readonly ot_id: string | null;
  readonly ot_numero: string | null;
  readonly quantite: string;
  readonly prix_unitaire: string;
  readonly remise: string | null;
  readonly taux_tva: string;
  /**
   * `transport` | `surcharge` | `annexe`. TROIS valeurs, DEUX groupes à
   * l'impression : la surcharge s'imprime dans « Description : » et entre dans
   * le sous-total transport. Posée par le bouton qui a créé la ligne, jamais
   * modifiable ensuite.
   */
  readonly categorie: string;
  /** La commande du client, UNE PAR LIGNE. Toujours nulle sur une surcharge. */
  readonly reference_client: string | null;
}

/** Une facture ouverte : l'en-tête et ses lignes. */
export interface FactureOuverte {
  readonly facture: Facture;
  readonly lignes: readonly LigneFacture[];
}

/**
 * ENVOYER UN ORDRE EN FACTURATION — le geste qui se décide.
 *
 * « Terminé » se déduit des étapes ; « envoyé en facturation » est un choix
 * humain, et c'est pour ça qu'il a une route à lui.
 */
export async function envoyerEnFacturation(
  jeton: string,
  ordreId: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(ordreId)}/facturation`,
    jeton,
    { methode: 'POST', corps: {} },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/** Retirer un ordre de la facturation, tant qu'aucune facture ne le porte. */
export async function retirerDeLaFacturation(
  jeton: string,
  ordreId: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(ordreId)}/facturation`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/** Les ordres qui attendent leur facture. */
export async function lireLesOrdresAFacturer(
  jeton: string,
): Promise<Resultat<readonly OrdreAFacturer[]>> {
  const echange = await appeler('/factures/a-facturer', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { ordres?: readonly OrdreAFacturer[] }).ordres ?? [],
  };
}

/**
 * FACTURER UNE SÉLECTION D'ORDRES. Rend les brouillons créés, dans l'ordre où
 * la page d'édition va les enchaîner.
 */
export async function facturerLesOrdres(
  jeton: string,
  otIds: readonly string[],
): Promise<Resultat<readonly Facture[]>> {
  const echange = await appeler('/factures', jeton, {
    methode: 'POST',
    corps: { ot_ids: otIds },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { factures?: readonly Facture[] }).factures ?? [],
  };
}

/** Toutes les pièces d'une période. */
export async function lireLesFactures(
  jeton: string,
  filtres: { readonly du?: string; readonly au?: string; readonly type?: string } = {},
): Promise<Resultat<readonly PieceListee[]>> {
  const parametres = new URLSearchParams();
  if (filtres.du !== undefined && filtres.du !== '') parametres.set('du', filtres.du);
  if (filtres.au !== undefined && filtres.au !== '') parametres.set('au', filtres.au);
  if (filtres.type !== undefined && filtres.type !== '') {
    parametres.set('type', filtres.type);
  }
  const suffixe = parametres.toString();
  const echange = await appeler(
    suffixe === '' ? '/factures' : `/factures?${suffixe}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { factures?: readonly PieceListee[] }).factures ?? [],
  };
}

/** Une facture et ses lignes. */
export async function lireUneFacture(
  jeton: string,
  id: string,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(`/factures/${encodeURIComponent(id)}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/** Corriger l'en-tête d'un brouillon. */
export async function modifierUneFacture(
  jeton: string,
  id: string,
  champs: Readonly<Record<string, string | null>>,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(`/factures/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/** Ajouter une ligne libre — une immobilisation, un lavage, une attente. */
export async function ajouterUneLigne(
  jeton: string,
  id: string,
  champs: Readonly<Record<string, string>>,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(`/factures/${encodeURIComponent(id)}/lignes`, jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/** Corriger une ligne. */
export async function modifierUneLigne(
  jeton: string,
  id: string,
  ligneId: string,
  champs: Readonly<Record<string, string | null>>,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(id)}/lignes/${encodeURIComponent(ligneId)}`,
    jeton,
    { methode: 'PATCH', corps: champs },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/** Retirer une ligne d'un brouillon. */
export async function supprimerUneLigne(
  jeton: string,
  id: string,
  ligneId: string,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(id)}/lignes/${encodeURIComponent(ligneId)}`,
    jeton,
    { methode: 'DELETE' },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/**
 * VALIDER — c'est ici, et nulle part ailleurs, que le numéro s'attribue.
 *
 * Un brouillon abandonné ne doit pas trouer la série : tant qu'on n'a pas
 * appuyé sur ce bouton, la pièce n'a pas de numéro.
 */
export async function validerUneFacture(
  jeton: string,
  id: string,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(id)}/validation`,
    jeton,
    {
      methode: 'POST',
      corps: {},
    },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/**
 * DÉCLARER QU'UNE FACTURE EST DÉJÀ FACTURÉE AILLEURS — 16/09/2026.
 *
 * ELLE N'APPELLE PAS PENNYLANE. Stafa enregistre seulement que la pièce existe
 * déjà là-bas, avec le numéro relevé dessus.
 *
 * `totalHt` EST RELEVÉ SUR LA PIÈCE, pas repris du brouillon : c'est une
 * double saisie, et c'est la seule vérification possible — l'identifiant
 * Pennylane est parti avec le brouillon supprimé. L'API refuse si les deux
 * totaux diffèrent.
 */
export async function declarerDejaFacturee(
  jeton: string,
  id: string,
  saisie: {
    numero: string;
    datePiece: string;
    echeance: string;
    totalHt: string;
  },
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(id)}/emission-externe`,
    jeton,
    {
      methode: 'POST',
      corps: {
        numero: saisie.numero,
        date_piece: saisie.datePiece,
        echeance: saisie.echeance,
        total_ht: saisie.totalHt,
      },
    },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/**
 * CORRIGER LES DEUX DATES D'UNE PIÈCE TRANSCRITE — 16/09/2026.
 *
 * Elle n'existe QUE pour les pièces déclarées déjà facturées ailleurs : Stafa
 * ne les a pas émises, il les transcrit, et une transcription se corrige. Une
 * facture émise par Stafa est refusée par l'API, avec le message qui renvoie à
 * l'avoir.
 *
 * Elle ne touche ni les lignes, ni les montants, ni le numéro.
 */
export async function corrigerLesDatesTranscrites(
  jeton: string,
  id: string,
  dates: { datePiece: string; echeance: string },
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(`/factures/${encodeURIComponent(id)}/dates`, jeton, {
    methode: 'PATCH',
    corps: { date_piece: dates.datePiece, echeance: dates.echeance },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/**
 * ENREGISTRER L'ENVOI, avec son canal. Cette route N'ENVOIE RIEN : elle note
 * qu'on a envoyé. Le service d'envoi n'existe pas encore.
 */
export async function enregistrerLEnvoi(
  jeton: string,
  id: string,
  canal: string,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(`/factures/${encodeURIComponent(id)}/envoi`, jeton, {
    methode: 'POST',
    corps: { canal },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/** Effacer un brouillon. Une pièce émise se corrige par un avoir. */
export async function supprimerUneFacture(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/factures/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

// -----------------------------------------------------------------------------
// Le recouvrement — 10/09/2026
// -----------------------------------------------------------------------------

/**
 * UNE SEULE LECTURE POUR TROIS ÉCRANS : poste client, relances, accueil.
 *
 * Ils regardent la même chose sous trois angles — ce qui est émis, ce qui est
 * encaissé, ce qui est gelé. Trois routes rendraient trois fois les mêmes
 * lignes et ouvriraient la porte à trois totaux qui ne tombent pas pareil.
 */
export async function lireLePoste(jeton: string): Promise<
  Resultat<{
    readonly pieces: readonly PieceRecouvrement[];
    readonly acomptes: readonly AcompteClient[];
    readonly gels: readonly GelClient[];
  }>
> {
  const echange = await appeler('/recouvrement/poste', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as {
    pieces?: readonly PieceRecouvrement[];
    acomptes?: readonly AcompteClient[];
    gels?: readonly GelClient[];
  };
  return {
    etat: 'ok',
    valeur: {
      pieces: corps.pieces ?? [],
      acomptes: corps.acomptes ?? [],
      gels: corps.gels ?? [],
    },
  };
}

/** Le journal des encaissements. */
export async function lireLesEncaissements(
  jeton: string,
  filtres: { readonly du?: string; readonly au?: string; readonly mode?: string } = {},
): Promise<Resultat<readonly EncaissementListe[]>> {
  const parametres = new URLSearchParams();
  if (filtres.du !== undefined && filtres.du !== '') parametres.set('du', filtres.du);
  if (filtres.au !== undefined && filtres.au !== '') parametres.set('au', filtres.au);
  if (filtres.mode !== undefined && filtres.mode !== '') {
    parametres.set('mode', filtres.mode);
  }
  const suffixe = parametres.toString();
  const echange = await appeler(
    suffixe === '' ? '/encaissements' : `/encaissements?${suffixe}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur:
      (echange.corps as { encaissements?: readonly EncaissementListe[] })
        .encaissements ?? [],
  };
}

/** Ce qu'un encaissement porte à l'écriture. */
export interface ChampsEncaissement {
  readonly client_id?: string;
  readonly recu_le?: string;
  readonly mode?: string;
  readonly reference?: string | null;
  readonly montant?: string;
  readonly note?: string | null;
  readonly affectations?: readonly {
    readonly facture_id: string;
    readonly montant: string;
  }[];
}

export async function saisirUnEncaissement(
  jeton: string,
  champs: ChampsEncaissement,
): Promise<Resultat<EncaissementListe>> {
  const echange = await appeler('/encaissements', jeton, {
    methode: 'POST',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as EncaissementListe };
}

export async function corrigerUnEncaissement(
  jeton: string,
  id: string,
  champs: ChampsEncaissement,
): Promise<Resultat<EncaissementListe>> {
  const echange = await appeler(`/encaissements/${encodeURIComponent(id)}`, jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as EncaissementListe };
}

export async function supprimerUnEncaissement(
  jeton: string,
  id: string,
): Promise<Resultat<null>> {
  const echange = await appeler(`/encaissements/${encodeURIComponent(id)}`, jeton, {
    methode: 'DELETE',
  });
  if (echange.etat === 'vide' || echange.etat === 'succes') {
    return { etat: 'ok', valeur: null };
  }
  return echec(echange);
}

/**
 * Geler les relances D'UNE FACTURE. La date de reprise est obligatoire.
 *
 * Sur la pièce et non sur le client — corrigé le 10/09/2026 : un client qui
 * conteste une facture continue d'être relancé sur les autres.
 */
export async function gelerLesRelances(
  jeton: string,
  factureId: string,
  champs: {
    readonly motif: string;
    readonly reprise_le: string;
    readonly note?: string | null;
  },
): Promise<Resultat<GelClient>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(factureId)}/gel`,
    jeton,
    {
      methode: 'POST',
      corps: champs,
    },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as GelClient };
}

export async function leverLeGel(
  jeton: string,
  factureId: string,
): Promise<Resultat<null>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(factureId)}/gel`,
    jeton,
    {
      methode: 'DELETE',
    },
  );
  if (echange.etat === 'vide' || echange.etat === 'succes') {
    return { etat: 'ok', valeur: null };
  }
  return echec(echange);
}

/** Une relance déjà partie, telle que l'historique la rend. */
export interface RelanceEnvoyee {
  readonly id: string;
  readonly client_id: string;
  readonly client_nom: string;
  readonly niveau: string;
  readonly envoyee_le: string;
  readonly canal: string;
  readonly montant_reclame: string;
  readonly envoyee_par: string;
  readonly pieces: readonly { readonly facture_id: string; readonly numero: string }[];
}

export async function lireLesRelances(
  jeton: string,
  clientId?: string,
): Promise<Resultat<readonly RelanceEnvoyee[]>> {
  const suffixe =
    clientId === undefined ? '' : `?client_id=${encodeURIComponent(clientId)}`;
  const echange = await appeler(`/relances${suffixe}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return {
    etat: 'ok',
    valeur: (echange.corps as { relances?: readonly RelanceEnvoyee[] }).relances ?? [],
  };
}

/**
 * MARQUER UNE RELANCE ENVOYÉE.
 *
 * « Produire » et « marquer envoyée » sont un seul geste — validé le 10/09.
 * Les séparer recréerait le trou d'aujourd'hui : des documents produits dont
 * personne ne sait s'ils sont partis.
 */
export async function marquerRelanceEnvoyee(
  jeton: string,
  champs: {
    readonly client_id: string;
    readonly niveau: string;
    readonly canal: string;
    readonly envoyee_le?: string;
    readonly montant_reclame: string;
    readonly facture_ids: readonly string[];
    readonly note?: string | null;
  },
): Promise<Resultat<RelanceEnvoyee>> {
  const echange = await appeler('/relances', jeton, { methode: 'POST', corps: champs });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as RelanceEnvoyee };
}

/**
 * ANNULER LA VALIDATION D'UNE FACTURE — elle redevient un brouillon et REND
 * SON NUMÉRO au compteur.
 *
 * L'API ne l'accepte que si la pièce porte le dernier numéro de sa série et
 * qu'aucun règlement ne s'y rattache : un numéro rendu au milieu d'une série
 * y laisserait un trou. Les deux refus ont leur code, et l'écran dit la suite.
 */
export async function annulerLaValidation(
  jeton: string,
  id: string,
): Promise<Resultat<FactureOuverte>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(id)}/validation`,
    jeton,
    {
      methode: 'DELETE',
    },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as FactureOuverte };
}

/**
 * UNE LIGNE QU'ON CHOISIT D'AVOIRER, avec son montant éventuellement redressé.
 *
 * `prix_unitaire` ABSENT veut dire « au montant d'origine ». L'envoyer
 * systématiquement obligerait l'écran à recopier une valeur qu'il n'a pas
 * modifiée — et à la déformer au passage, au premier arrondi d'affichage.
 *
 * LE SIGNE N'EST JAMAIS ICI. On envoie un montant POSITIF ; c'est l'API qui
 * pose le moins. L'écran qui déciderait du signe serait un second endroit où
 * se tromper, et le premier avoir faux ne se rattraperait que par un autre
 * avoir.
 */
export interface LigneAAvoirer {
  readonly id: string;
  readonly quantite?: string;
  readonly prix_unitaire?: string;
}

/**
 * ÉTABLIR UN AVOIR SUR UNE FACTURE ÉMISE — 11/09/2026.
 *
 * Rend un BROUILLON d'avoir. Rien n'est validé, rien ne part chez Pennylane :
 * la suite est celle de n'importe quelle pièce, aperçu puis validation.
 *
 * `lignes` ABSENT VEUT DIRE « TOUT », et ce n'est pas un raccourci d'écriture :
 * un avoir total qui listerait ses lignes deviendrait faux le jour où la
 * facture en gagnerait une entre l'ouverture de l'écran et le clic.
 *
 * QUATRE REFUS PORTENT LEUR PROPRE SUITE — `avoir_sur_avoir`,
 * `facture_deja_avoiree`, `avoir_trop_grand` (dont le message donne le
 * plafond en euros) et `avoir_impossible`.
 */
export async function etablirUnAvoir(
  jeton: string,
  id: string,
  motif: string,
  lignes?: readonly LigneAAvoirer[],
): Promise<ResultatDetaille<FactureOuverte>> {
  const echange = await appeler(`/factures/${encodeURIComponent(id)}/avoir`, jeton, {
    methode: 'POST',
    corps: lignes === undefined ? { motif } : { motif, lignes },
  });
  if (echange.etat === 'succes') {
    return { etat: 'ok', valeur: echange.corps as FactureOuverte };
  }
  /**
   * `ResultatDetaille` ET NON `Resultat`, comme l'envoi chez Pennylane et pour
   * la MÊME raison : le texte est ici une donnée, pas une formulation.
   * `avoir_trop_grand` porte LE PLAFOND EN EUROS — « cet avoir dépasse ce qui
   * reste à avoirer : 5 174,58 € HT au plus ». Le perdre obligerait à tâtonner
   * sur un montant, sur un geste irréversible.
   */
  const brut = echec(echange);
  const corps =
    echange.etat === 'refus' && typeof echange.corps === 'object' && echange.corps !== null
      ? (echange.corps as { erreur?: unknown })
      : {};
  return {
    etat: 'echec',
    cause: brut.cause,
    detail: typeof corps.erreur === 'string' ? corps.erreur : null,
  };
}

// ---------------------------------------------------------------------------
// Le rattachement des clients à Pennylane — 11/09/2026
// ---------------------------------------------------------------------------

/**
 * CE QUE L'ÉCRAN DE RATTACHEMENT REÇOIT, en un seul appel.
 *
 * Un seul appel, et non deux, parce que l'écran n'a rien à montrer tant qu'il
 * n'a pas les deux listes : afficher les clients Stafa pendant que Pennylane
 * charge donnerait un tableau où TOUTES les lignes sembleraient « à
 * rattacher », y compris celles qui ne le sont pas.
 */
export interface Rattachements {
  readonly clients: readonly ClientARattacher[];
  readonly pennylane: {
    readonly clients: readonly ClientPennylane[];
    /** Combien d'allers-retours il a fallu pour ramasser la liste. */
    readonly pages: number;
    /** La liste est INCOMPLÈTE. L'écran doit le dire, pas le taire. */
    readonly tronque: boolean;
    readonly recus: number;
    readonly retenus: number;
    /** Les CLÉS du premier client reçu — pour savoir ce que la liste porte. */
    readonly champs_recus: readonly string[] | null;
    readonly message: string | null;
  };
}

export async function lireRattachements(
  jeton: string,
): Promise<Resultat<Rattachements>> {
  const echange = await appeler('/pennylane/rattachements', jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as Rattachements };
}

/**
 * Rattacher un client Stafa à son client Pennylane — ou l'en DÉTACHER.
 *
 * `null` DÉTACHE, et c'est le contrat de la modification partielle : un champ
 * absent n'est pas touché, un champ à `null` est effacé. La croix de l'écran
 * envoie donc `null`, et non une chaîne vide.
 *
 * L'IDENTIFIANT VOYAGE EN CHAÎNE. La colonne est un `bigint` ; le mettre en
 * nombre le ferait passer par la représentation flottante de JavaScript, qui
 * arrondit au-delà de 2^53 sans rien dire. L'API accepte les deux formes et
 * n'en garde qu'une.
 */
export async function rattacherAPennylane(
  jeton: string,
  clientId: string,
  pennylaneId: string | null,
): Promise<Resultat<Dossier>> {
  return modifierFiche(
    jeton,
    // Le chemin de l'annuaire des clients, écrit ici et non importé
    // d'`Application.tsx` : cette fonction ne dessine rien et n'a aucune raison
    // de dépendre d'un composant.
    { chemin: 'clients', cleListe: 'clients', cleFiche: 'client' },
    clientId,
    { pennylane_client_id: pennylaneId },
  );
}

// ---------------------------------------------------------------------------
// L'aperçu Pennylane — 11/09/2026
// ---------------------------------------------------------------------------

/** Ce que rend l'envoi d'une facture chez Pennylane. */
export interface EnvoiPennylane {
  readonly pennylane_facture_id: string | null;
  /**
   * Faux veut dire que le brouillon EXISTE chez eux mais que Stafa ne l'a pas
   * retenu. L'écran doit pouvoir le dire : le prochain envoi ne saurait pas
   * supprimer celui-là, et il en resterait deux.
   */
  readonly identifiant_garde: boolean;
  readonly suppression: {
    readonly tentee: boolean;
    readonly reussie: boolean;
    readonly statut: number | null;
  };
  /** Calculés par PENNYLANE, pas par Stafa. */
  readonly ht: string | null;
  readonly tva: string | null;
  readonly ttc: string | null;
  /** Vide sur un brouillon. Il se pose à la finalisation. */
  readonly numero: string | null;
}

/**
 * ENVOIE LA FACTURE CHEZ PENNYLANE et rend ce qu'ils en ont fait.
 *
 * Le brouillon précédent, s'il existe, est supprimé avant : sans quoi chaque
 * correction laisserait une pièce de plus derrière elle.
 */
/**
 * UN RÉSULTAT QUI PORTE AUSSI LE DÉTAIL DU REFUS.
 *
 * `Resultat<T>` ne transporte qu'un CODE, et c'est la bonne règle pour tout le
 * reste du dépôt : l'écran décide sur le code, jamais sur le texte français,
 * qui peut être reformulé sans préavis.
 *
 * ICI LE TEXTE EST UNE DONNÉE, pas une formulation. Quand Pennylane refuse une
 * pièce, SON message est la seule source qui dise ce qui ne va pas — « Custom
 * payment reference has already been taken » a fait gagner une heure le 11/09.
 * Le perdre en route laisserait l'écran afficher « impossible » sans plus.
 *
 * Le code reste ce sur quoi l'écran DÉCIDE ; le détail est ce qu'il MONTRE.
 */
export type ResultatDetaille<T> =
  | { readonly etat: 'ok'; readonly valeur: T }
  | {
      readonly etat: 'echec';
      readonly cause: EchecApi;
      readonly detail: string | null;
    };

export async function envoyerAPennylane(
  jeton: string,
  factureId: string,
): Promise<ResultatDetaille<EnvoiPennylane>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(factureId)}/pennylane`,
    jeton,
    { methode: 'POST' },
  );
  if (echange.etat === 'succes') {
    return { etat: 'ok', valeur: echange.corps as EnvoiPennylane };
  }
  const brut = echec(echange);
  const corps =
    echange.etat === 'refus' && typeof echange.corps === 'object' && echange.corps !== null
      ? (echange.corps as { erreur?: unknown })
      : {};
  return {
    etat: 'echec',
    cause: brut.etat === 'echec' ? brut.cause : 'service',
    detail: typeof corps.erreur === 'string' ? corps.erreur : null,
  };
}


// ---------------------------------------------------------------------------
// Les annexes de la facture — 14/09/2026
// ---------------------------------------------------------------------------

/** Une pièce candidate à l'envoi avec la facture. Le CHEMIN, jamais le fichier. */
export interface PieceAnnexe {
  readonly id: string;
  readonly nom: string;
  readonly chemin: string;
  /** Le nom du type — « Lettre de voiture ». Nul si le type a été supprimé. */
  readonly type: string | null;
  /**
   * FAUX = retirée à la main de CETTE facture. La pièce reste dans la liste,
   * décochée : la faire disparaître au moment où on la décoche ôterait tout
   * moyen de la remettre.
   */
  readonly partira: boolean;
}

/** Ce que le dépôt rend, pièce par pièce. */
export interface RapportAnnexes {
  readonly deposees: number;
  readonly attendues: number;
  readonly pieces: readonly {
    readonly document_id: string;
    readonly nom: string;
    readonly depose: boolean;
    readonly cause: string | null;
  }[];
}

/** CE QUI PARTIRAIT avec cette facture. Sert à l'annoncer avant d'envoyer. */
export async function lireAnnexesPrevues(
  jeton: string,
  factureId: string,
): Promise<Resultat<readonly PieceAnnexe[]>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(factureId)}/pennylane/annexes`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { pieces?: readonly PieceAnnexe[] };
  return { etat: 'ok', valeur: corps.pieces ?? [] };
}


// ---------------------------------------------------------------------------
// Le compte rendu d'un client — 14/09/2026
// ---------------------------------------------------------------------------

/**
 * CE QU'IL FAUT SAVOIR D'UN CLIENT QUAND IL APPELLE, et que la liste du poste
 * client n'a pas.
 *
 * NI LES PIÈCES, NI LES TOTAUX : l'écran les tient déjà de `balanceAgee()`,
 * calculée sur `GET /recouvrement/poste`. Les redemander ici ferait deux
 * chemins pour le même chiffre, et un jour deux chiffres.
 */
export interface CompteRenduClient {
  readonly client: {
    readonly id: string;
    readonly nom: string;
    readonly code: string | null;
    readonly conditions_paiement: string | null;
    readonly courriel_facturation: string | null;
    readonly courriel: string | null;
    readonly telephone: string | null;
    readonly encours_autorise: string | null;
  };
  readonly contacts: readonly {
    readonly id: string;
    readonly nom: string;
    readonly fonction: string | null;
    readonly telephone: string | null;
    readonly courriel: string | null;
  }[];
  readonly reglements: readonly {
    readonly id: string;
    readonly recu_le: string;
    readonly mode: string;
    readonly reference: string | null;
    readonly montant: string;
    readonly affecte: string;
    /** Les numéros des pièces soldées, séparés par des virgules. */
    readonly pieces: string | null;
  }[];
  /**
   * LE FIL DES FAITS, assemblé par l'API des quatre tables qui les datent —
   * l'émission, la relance, le gel, le règlement. `genre` dit lequel ; le
   * texte français se compose À L'ÉCRAN, jamais en base.
   */
  readonly historique: readonly {
    readonly genre: string;
    readonly jour: string;
    readonly piece: string | null;
    readonly par: string | null;
    readonly valeur: string | null;
    readonly precision: string | null;
  }[];
}

export async function lireCompteRendu(
  jeton: string,
  clientId: string,
): Promise<Resultat<CompteRenduClient>> {
  const echange = await appeler(
    `/recouvrement/poste/${encodeURIComponent(clientId)}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as CompteRenduClient };
}

/**
 * RETIRE OU REMET une pièce sur cette facture. Rend la liste à jour.
 *
 * LE CHOIX S'ENREGISTRE, et c'est la raison d'être de la route : un second
 * aperçu supprime le brouillon chez Pennylane et le recrée, annexes comprises.
 * Un décochage qui ne vivrait que dans l'écran ferait revenir la pièce sans
 * que personne ne l'ait demandé.
 */
export async function changerPieceAnnexe(
  jeton: string,
  factureId: string,
  documentId: string,
  partira: boolean,
): Promise<Resultat<readonly PieceAnnexe[]>> {
  const echange = await appeler(
    `/factures/${encodeURIComponent(factureId)}/pennylane/annexes/${encodeURIComponent(documentId)}`,
    jeton,
    { methode: 'PATCH', corps: { partira } },
  );
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { pieces?: readonly PieceAnnexe[] };
  return { etat: 'ok', valeur: corps.pieces ?? [] };
}

/**
 * DÉPOSE LES PIÈCES chez Pennylane, à la suite de l'envoi de la facture.
 *
 * POURQUOI LE NAVIGATEUR SIGNE, ET PAS L'API. Le Worker ne peut pas lire un
 * fichier du rangement : la clé de service Supabase est interdite dans ce
 * dépôt (cadrage §4). C'est donc ici, avec la session de la personne
 * connectée, qu'on demande au rangement une adresse valable cinq minutes par
 * pièce ; l'API va ensuite chercher le fichier avec cette adresse.
 *
 * UNE SIGNATURE QUI ÉCHOUE N'ARRÊTE PAS LES AUTRES : l'adresse manque, et
 * l'API le rapporte en « adresse_absente » plutôt que de taire la pièce.
 */
export async function deposerLesAnnexes(
  jeton: string,
  factureId: string,
  pieces: readonly PieceAnnexe[],
): Promise<Resultat<RapportAnnexes>> {
  const adresses: Record<string, string> = {};
  // ON NE SIGNE QUE CE QUI PART. L'API refiltre de son côté — c'est elle qui
  // tranche — mais demander au rangement de signer une pièce qu'on vient de
  // retirer serait du travail pour rien, et un jeton de plus en circulation.
  for (const piece of pieces.filter((p) => p.partira)) {
    const signee = await adresseDuFichier(jeton, piece.chemin);
    if (signee.etat === 'ok') adresses[piece.id] = signee.valeur;
  }

  const echange = await appeler(
    `/factures/${encodeURIComponent(factureId)}/pennylane/annexes`,
    jeton,
    { methode: 'POST', corps: { adresses } },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as RapportAnnexes };
}

/**
 * LE DOCUMENT, EN OCTETS, servi par Stafa.
 *
 * PAS DE `appeler()` ICI, et c'est la seule route du fichier dans ce cas :
 * cette réponse n'est pas du JSON. On lit donc la réponse brute.
 *
 * L'écran en fera une adresse `blob:`. Un `iframe` ne sait pas porter d'en-tête
 * `Authorization` ; mettre le jeton dans l'adresse aurait été plus court et
 * l'aurait laissé traîner dans l'historique du navigateur.
 */
export async function lirePdfPennylane(
  jeton: string,
  factureId: string,
): Promise<Resultat<Blob>> {
  let reponse: Response;
  try {
    reponse = await fetch(
      `${configuration.apiUrl}/factures/${encodeURIComponent(factureId)}/pennylane/pdf`,
      { headers: { Authorization: `Bearer ${jeton}` } },
    );
  } catch {
    return { etat: 'echec', cause: 'injoignable' };
  }

  if (!reponse.ok) {
    // Le corps d'un refus, lui, EST du JSON — on y lit le code.
    let code: unknown;
    try {
      code = ((await reponse.json()) as { code?: unknown }).code;
    } catch {
      code = undefined;
    }
    if (reponse.status === 401 || reponse.status === 403) {
      return { etat: 'echec', cause: 'acces' };
    }
    if (code === 'introuvable') return { etat: 'echec', cause: 'introuvable' };
    return { etat: 'echec', cause: 'pennylane_impossible' };
  }

  return { etat: 'ok', valeur: await reponse.blob() };
}

// ---------------------------------------------------------------------------
// Les journées — la file de travail du bouclage
// ---------------------------------------------------------------------------
// Les TYPES vivent dans `journees.ts`, avec les fonctions pures qui en tirent
// un état : ici on ne fait que transporter. Les redéclarer donnerait deux
// vérités pour une même forme, et elles divergeraient.

import type {
  ArretDeLaJournee,
  CamionDuJour,
  EnTeteJournee,
  JourListe,
  OrdreDuJour,
  SegmentLu,
} from './journees.js';

/**
 * Les jours qui portent au moins une étape.
 *
 * `etat` vaut « ouvertes » (défaut) ou « toutes ». Une journée clôturée sort de
 * la file — elle est figée — mais reste consultable.
 */
export async function lireJournees(
  jeton: string,
  etat: 'ouvertes' | 'toutes' = 'ouvertes',
): Promise<Resultat<readonly JourListe[]>> {
  const echange = await appeler(`/journees?etat=${etat}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { journees?: readonly JourListe[] };
  return { etat: 'ok', valeur: corps.journees ?? [] };
}

/** Les camions d'un jour. Le jour s'écrit `AAAA-MM-JJ`. */
export async function lireLesCamionsDuJour(
  jeton: string,
  jour: string,
): Promise<Resultat<readonly CamionDuJour[]>> {
  const echange = await appeler(`/journees/${encodeURIComponent(jour)}`, jeton);
  if (echange.etat !== 'succes') return echec(echange);
  const corps = echange.corps as { camions?: readonly CamionDuJour[] };
  return { etat: 'ok', valeur: corps.camions ?? [] };
}

/**
 * LA JOURNÉE D'UN CAMION — le troisième niveau, 14/09/2026.
 *
 * Elle rend QUATRE listes et aucun calcul : l'en-tête, les arrêts dans
 * l'ordre, les trajets déjà mesurés, et les ordres du jour (les cibles
 * possibles d'un péage). La chaîne des trajets se déduit dans `journees.ts`.
 */
/** La part d'un service dans le coût d'une journée. */
export interface PartDeService {
  readonly centre_id: string;
  readonly nom: string;
  readonly part: number;
  readonly cout_journalier_centimes: number;
}

/** Ce que la route du pointage rend en plus des arrêts et des trajets. */
/** Une part du chiffre d'affaires d'une journée — lot 143 (voir `resultat.ts`). */
export interface PartDeCa {
  readonly ot_id: string;
  readonly ca_ordre_centimes: number;
  readonly km_jour: number;
  readonly base_km: number | null;
  readonly deja_reparti_centimes: number | null;
  readonly regle: 'roule' | 'distance' | 'reste' | 'seul';
  readonly part_centimes: number;
}

export interface ResultatDeLaJournee {
  readonly ca_centimes: number | null;
  readonly pourquoi_sans_ca: string | null;
  /** Le détail ordre par ordre — facultatif pour un serveur d'avant le lot 143. */
  readonly detail_ca?: readonly PartDeCa[];
  readonly km: number | null;
  readonly km_releves: number;
  readonly km_vide: number | null;
  readonly trajets_manquants: number;
  readonly peage_centimes: number;
  readonly depenses: {
    readonly journee: number;
    readonly parts: readonly PartDeService[];
    readonly gazole: number;
    readonly frais_km: number;
    readonly frais_heure: number;
    readonly peage: number;
    readonly total: number;
    readonly manques: readonly string[];
  } | null;
  readonly resultat_centimes: number | null;
  readonly pourquoi_sans_cout: string | null;
  readonly parametrage_manquant: readonly string[];
}

export interface JourneeDuCamion {
  readonly journee: EnTeteJournee;
  readonly etapes: readonly ArretDeLaJournee[];
  readonly segments: readonly SegmentLu[];
  readonly ordres: readonly OrdreDuJour[];
  /**
   * LE COÛT ET LA MARGE DE CETTE SEULE JOURNÉE — 30/10/2026.
   *
   * `null` quand la journée n'a aucun arrêt : elle n'existe pas pour le
   * calcul (décision du 13/09, « un jour existe s'il porte au moins une
   * étape »), et l'écran doit le dire plutôt qu'afficher des zéros.
   *
   * Le type complet vit dans `VueJourneeDuCamion.tsx`, à côté de ce qui
   * l'affiche ; ici on ne garde que ce qui traverse le réseau.
   */
  readonly resultat: ResultatDeLaJournee | null;
}

function cheminJournee(jour: string, vehiculeId: string): string {
  return `/journees/${encodeURIComponent(jour)}/${encodeURIComponent(vehiculeId)}`;
}

export async function lireLaJourneeDuCamion(
  jeton: string,
  jour: string,
  vehiculeId: string,
): Promise<Resultat<JourneeDuCamion>> {
  const echange = await appeler(cheminJournee(jour, vehiculeId), jeton);
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as JourneeDuCamion };
}

/**
 * L'ORDRE DES ARRÊTS. La liste envoyée DEVIENT l'ordre — elle doit donc
 * contenir TOUS les arrêts de la journée, et l'API le refuse sinon.
 */
export async function ordonnerLesArrets(
  jeton: string,
  jour: string,
  vehiculeId: string,
  etapes: readonly string[],
): Promise<Resultat<null>> {
  const echange = await appeler(`${cheminJournee(jour, vehiculeId)}/etapes`, jeton, {
    methode: 'PUT',
    corps: { etapes },
  });
  // UN 204 N'A PAS DE CORPS : `vide` EST LE SUCCÈS de cette route — lot 138,
  // 02/11/2026. Avant, il tombait dans `echec()` et s'affichait « Stafa ne
  // répond pas », alors que l'écriture avait bien eu lieu.
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/** Les kilomètres d'un trajet, désigné par ses DEUX extrémités. */
export async function enregistrerUnTrajet(
  jeton: string,
  jour: string,
  vehiculeId: string,
  trajet: {
    readonly depuis_etape_id: string | null;
    readonly vers_etape_id: string | null;
    readonly km_reels?: number | null;
    readonly km_estimes?: number | null;
    /** LE PÉAGE EST SUR LA ROUTE depuis le 14/09/2026. */
    readonly peage?: string | null;
    /** `null` = toute la journée. Un choix, pas une absence. */
    readonly peage_ot_id?: string | null;
  },
): Promise<Resultat<null>> {
  const echange = await appeler(`${cheminJournee(jour, vehiculeId)}/segments`, jeton, {
    methode: 'PUT',
    corps: trajet,
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/** La journée elle-même : lieu de fin, heures, consommation, pointage, clôture. */
export async function modifierLaJournee(
  jeton: string,
  jour: string,
  vehiculeId: string,
  champs: {
    /** Le lieu de début CORRIGÉ. `null` rend la main à la déduction. */
    readonly debut_site_id?: string | null;
    readonly fin_site_id?: string | null;
    readonly debut_le?: string | null;
    readonly fin_le?: string | null;
    readonly consommation?: string | null;
    readonly pointee?: boolean;
    readonly cloturee?: boolean;
  },
): Promise<Resultat<null>> {
  const echange = await appeler(cheminJournee(jour, vehiculeId), jeton, {
    methode: 'PATCH',
    corps: champs,
  });
  // UN 204 N'A PAS DE CORPS : `vide` EST LE SUCCÈS de cette route — lot 138,
  // 02/11/2026. Constaté par Mohamed en production : choisir un lieu de début
  // ou de fin affichait « Stafa ne répond pas », sans rien dans la console,
  // alors que l'écriture avait bien eu lieu. `PATCH /journees/:jour/:vehicule`
  // répond 204, `appeler` le rend `vide`, et `vide` tombait dans `echec()`,
  // dont le repli est `injoignable`. Même défaut pour la consommation, le
  // pointage et la clôture, qui passent par la même route.
  if (echange.etat !== 'succes' && echange.etat !== 'vide') return echec(echange);
  return { etat: 'ok', valeur: null };
}

/**
 * LE RÉSULTAT DES JOURNÉES — 17/09/2026, lot C.
 *
 * Un seul appel sert les quatre vues (jours, camions d'un jour, attelages,
 * services) : les regroupements se font à l'écran, dans `resultat.ts`. Quatre
 * appels pour quatre façons de lire les mêmes journées, c'est quatre chances
 * d'obtenir quatre totaux différents.
 *
 * L'API borne la période à 92 jours et refuse au-delà.
 */
export async function lireLeResultatDesJournees(
  jeton: string,
  du: string,
  au: string,
): Promise<Resultat<ResultatLu>> {
  const echange = await appeler(
    `/resultat/journees?du=${encodeURIComponent(du)}&au=${encodeURIComponent(au)}`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as ResultatLu };
}

/**
 * LIRE UNE CONFIRMATION DE TRANSPORT — lot 124.
 *
 * LA ROUTE N'ÉCRIT RIEN. C'est un `POST` parce qu'un document voyage dans le
 * corps, pas parce que quelque chose change : l'écriture reste à
 * `POST /ordres-transport`, depuis ce que l'exploitation aura validé.
 *
 * DEUX SORTES D'ÉCHEC, ET ELLES NE SE SOIGNENT PAS PAREIL :
 *
 *   - l'appel échoue (réseau, jeton, refus) → `etat: 'echec'`, comme partout ;
 *   - l'appel RÉUSSIT mais la lecture n'a pas eu lieu → `etat: 'ok'` avec
 *     `lu: false` et un message. C'est le cas quand la clé n'est pas posée,
 *     quand le modèle ne répond pas, ou quand le document est refusé.
 *
 * LA SECONDE N'EST PAS UNE PANNE DE STAFA, et l'écran ne doit pas la
 * présenter comme telle : il dit ce qui s'est passé et propose la saisie à la
 * main. Les mêler aurait fait afficher « Stafa ne répond pas » le jour où
 * c'est simplement la clé qui manque.
 */
export async function lireConfirmation(
  jeton: string,
  document: { readonly base64: string; readonly type: string },
): Promise<Resultat<LectureFaite>> {
  const echange = await appeler('/confirmations/lire', jeton, {
    methode: 'POST',
    corps: { document: document.base64, type: document.type },
  });
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as LectureFaite };
}
